/**
 * Body Map: every body part of a jump shot, read from pose landmarks.
 *
 * Spec: BODY-MAP.md at the repo root. This file is the maths and the words;
 * public/js/bodymap.js is the screen. Pure: no DOM, no clock, no storage, no
 * network. It loads in the page bundle, in the Worker and in Node
 * (scripts/bodyparts-test.mjs), and it imports nothing.
 *
 * Rules that run through every line (HANDOFF.md):
 * - Measured means this code worked it out from landmarks. Every reading
 *   carries how sure it is, and a reading the camera could not really see
 *   never gets a verdict (section 1.4 of the spec).
 * - Movement only. The head is read from the nose and the ears, the way
 *   headYaw() does it. No face, eye, mood or emotion reading, ever.
 * - Never medical. Form only. Every drill ends with the safety line.
 * - Every threshold below is marked GUESS (new, untuned) or REUSE (the same
 *   number the app already scores with). None has been checked against real
 *   court footage yet.
 *
 * Units. MediaPipe image points are 0..1 of the frame WIDTH for x and of the
 * frame HEIGHT for y, which are not square. Every image measurement here
 * first multiplies x by the frame's aspect ratio (width over height), so one
 * unit is one frame height in both directions and angles do not change with
 * the camera's shape (spec section 0). World points (lm.world) are metres
 * about the hips: x to the image right, y DOWN, z away from the camera.
 */

/* ======================================================================= *
 *  COPIED CONSTANTS                                                        *
 *  Copied, not imported: public/scoring.js is being edited by another      *
 *  workflow, and this file has to load without it (Worker, Node, page).    *
 *  Each one names its source. If it changes there, change it here too.     *
 * ======================================================================= */
export const SRC = {
  ARM_RISE: 0.15,                 // scoring.js TUNABLES.ARM_RISE
  FOLLOW_ELBOW_ANGLE: 140,        // scoring.js TUNABLES.FOLLOW_ELBOW_ANGLE
  FOLLOW_MS_IDEAL: 400,           // scoring.js TUNABLES.FOLLOW_MS_IDEAL
  MIN_VISIBILITY: 0.5,            // scoring.js TUNABLES.POSE.MIN_VISIBILITY
  MIN_SHOULDER_FRAC: 0.045,       // scoring.js TUNABLES.MIN_SHOULDER_FRAC
  ELBOW_OFFSET_PERFECT: 0.08,     // scoring.js TUNABLES.ELBOW_OFFSET_PERFECT
  ELBOW_OFFSET_ZERO: 0.45,        // scoring.js TUNABLES.ELBOW_OFFSET_ZERO
  RELEASE_ANGLE_IDEAL: 62,        // scoring.js TUNABLES.RELEASE_ANGLE_IDEAL
  RELEASE_ANGLE_ZERO_AT: 40,      // scoring.js TUNABLES.RELEASE_ANGLE_ZERO_AT
  LEAN_BEST: 8, LEAN_ZERO: 38,    // scoring.js TUNABLES.BALANCE.LEAN_BEST / LEAN_ZERO
  OFFSET_BEST: 0.25,              // scoring.js TUNABLES.BALANCE.OFFSET_BEST
  OFFSET_ZERO: 1.15,              // scoring.js TUNABLES.BALANCE.OFFSET_ZERO
  MIN_BASE: 0.35,                 // scoring.js TUNABLES.BALANCE.MIN_BASE
  LIFT: 0.12,                     // scoring.js TUNABLES.FOOTWORK.LIFT
  NOISE_PCT: 4,                   // scoring.js TUNABLES.FATIGUE.NOISE_PCT
  HEAD_MIN_SHOULDER_FRAC: 0.09,   // scoring.js HEAD.MIN_SHOULDER_FRAC (also lockcrop.js SMALL)
  HEAD_SQUARE: 0.12,              // scoring.js HEAD.SQUARE
  // posemetrics.js COPY.rough, word for word, shown until the player has calibrated.
  ROUGH_LINE: 'These scores are a rough guess for now. Do the quick setup and they fit you.',
};

/* ======================================================================= *
 *  THRESHOLDS. Every number the Body Map decides with.                     *
 * ======================================================================= */
export const BODY = {
  TV: 1,                    // thresholds version, stored on every snapshot and session total

  // 1.2 status bands, tied to the Element tiers (REUSE: Electricity starts at 70, Water at 40)
  GOOD: 70, OKAY: 40,

  // 1.4 confidence
  VERDICT: 0.6,             // conf at or over this colours the part. GUESS
  ROUGH: 0.4,               // 0.4 to 0.6 is a grey rough read, under it only "could not see". GUESS
  VIS_LO: 0.5,              // REUSE POSE.MIN_VISIBILITY: under this a joint is not seen
  VIS_HI: 0.9,              // visibility term is 1 from here up. GUESS
  TRUST: { high: 1, medium: 0.8, low: 0.55, none: 0 },  // GUESS
  NEAR_SWF: 0.09,           // REUSE HEAD.MIN_SHOULDER_FRAC: shoulders under this share of the frame width is "far"
  FAR_TERM: 0.85,           // GUESS
  FPS_OK: 24, FPS_MIN: 15, FPS_SLOW_TERM: 0.75,          // GUESS
  HAND_SCORE: 0.7,          // GUESS
  HAND_PX_OK: 100, HAND_PX_MIN: 60, HAND_SMALL_TERM: 0.8, // hand size in camera pixels, trackmath.handSize. GUESS
  HAND_NEAR_WRIST: 0.6,     // a hand whose wrist sits further than this many hand sizes from the pose wrist is someone else's. GUESS
  TURN_DEG: 30, TURN_TERM: 0.8,                          // GUESS
  PART_MIN_SHOTS: 3,        // shots with a status before a part glows for a session. GUESS

  // 1.5 camera view
  Q0: 0.72,                 // shoulder width over torso seen square on. GUESS until calibration
  Q0_HIP: 0.5,              // the same for the hips. GUESS
  YAW_WORLD_W: 0.6,         // weight of the world estimate in the blend. GUESS
  FRONT_MAX: 30, ANGLED_MAX: 60,                         // degrees. GUESS

  // 1.3 key frames
  SET_BACK_MS: 700, SET_FALLBACK_MS: 400, SET_FALLBACK_RISE: -0.3,   // GUESS
  LOAD_BACK_MS: 1200,       // GUESS
  HIPS_STILL: 0.02,         // hips moving less than this many body units never dipped. GUESS
  TAKEOFF_MS: 700,          // no lift off within this long after the dip is a set shot. GUESS
  FOLLOW_MAX_MS: 400, FOLLOW_DEFAULT_MS: 150,            // GUESS
  MEDIAN_MS: 50,            // neighbours this close are folded into a key frame's median. GUESS
  EXT_SMOOTH_MS: 40, EXT_FIT_MS: 60,                     // how an extreme (dip, set, follow) is found through jitter. GUESS
  FOOT_UP: 0.04,            // a third of FOOTWORK.LIFT: a shooting hop is smaller than a step. GUESS
  FOOT_DOWN: 0.02,          // GUESS
  FOOT_RUN_MS: 50,          // a foot has to hold up or down for 2 frames, and at least this long. GUESS
  FOOT_REF_MS: 600,         // the floor level is read over this long before the dip. GUESS
  EARLY_HEEL_MS: 50,        // heels up this long before the dip caps the heel score. GUESS
  STAND_P: 0.6, STAND_MS: 4000,                          // REUSE createPlayerScorer's jump level tracker
  EXT_MS: 100, BEND_MS: 150,                             // GUESS
  STICK_MS: 600, STICK_OFFSET_MS: 400, STICK_MIN_MS: 300, // GUESS
  NO_JUMP_END_MS: 300,      // with no jump, drift is read from the dip to this long after the follow through. GUESS

  // cross checks and caps
  FLARE_CROSS: 0.6,         // conf multiplier when elbow flare and elbow line disagree by two tiers. GUESS
  PALM_EDGE: 0.45,          // knuckle width over palm length under this is a palm seen edge on. GUESS
  HOLD_FLEX: 30,            // wrist bent down at least this far counts as the goose neck held. GUESS
  // conf cap when the hold falls back to the arm alone. The spec says 0.7, which
  // is a verdict: the Fingers part would then glow from a phone under the hoop,
  // where the ball hides the fingers (the spec's own preview story has them
  // unseen from the front). Under VERDICT, the arm hold is shown as a grey
  // rough read and never colors the fingers. GUESS
  HOLD_ARM_CAP: 0.55,
  CAP_SCORE: 55,            // an extra step, or heels up before the dip, caps that score here. GUESS

  // 2.5 aggregates
  TREND_N: 8, TREND_BEFORE: 3, POOL_N: 3,
  ARROW: 5,                 // a trend arrow needs a change of 5 points. GUESS, in the spirit of FATIGUE.NOISE_PCT 4

  // 3.7 XP and badges
  XP_PER_GOOD: 2, XP_GOOD_CAP: 20, XP_TIER: 15, XP_JUMP: 40, XP_PB: 10, XP_LEVEL: 200,  // GUESS
  BADGE_GOOD: 10,           // good shots for one part in one session. GUESS
  STREAK_SHOTS: 5, STREAK_PARTS: 6,                      // GUESS
};

/** 2.4 and 2.3: how much is kept. BUFFER_MS is 4000, not the spec's 3000: the
 *  dip window reaches 1900 ms before release, the arm can stay up 1600 ms
 *  after it before the detector reports the shot, and the standing hip level
 *  needs the seconds before the dip. */
export const KEEP = { SNAPS: 40, SNAP_MAX_BYTES: 6144, SNAP_TYPICAL_BYTES: 4608, BUFFER_MS: 4000, WAIT_LAND_MS: 1200, PRE_MS: 2300, BATCH: 12, REPS: 200, IDS: 300, BEST: 11, CELEBRATED: 200, VIEW_SESSIONS: 20 };

export const MOMENTS = ['load', 'set', 'takeoff', 'release', 'follow', 'land'];
export const VIEWS = ['front', 'back', 'side', 'angled'];
export const STATUSES = ['good', 'okay', 'work'];

/** 2.1: the 21 body points a snapshot keeps. Nose and ears only from the face, for head direction. */
export const KEEP_POINTS = [0, 7, 8, 11, 12, 13, 14, 15, 16, 19, 20, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32];

/** Bones of the kept points, for the replay. */
export const BONES = [
  [11, 12], [11, 23], [12, 24], [23, 24],
  [11, 13], [13, 15], [15, 19], [12, 14], [14, 16], [16, 20],
  [23, 25], [25, 27], [24, 26], [26, 28],
  [27, 29], [29, 31], [27, 31], [28, 30], [30, 32], [28, 32],
];
export const HAND_BONES = [[0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [5, 6], [6, 7], [7, 8], [5, 9], [9, 10], [10, 11], [11, 12], [9, 13], [13, 14], [14, 15], [15, 16], [13, 17], [17, 18], [18, 19], [19, 20], [0, 17]];

const SIDES = {
  right: { S: 12, E: 14, W: 16, pinky: 18, index: 20, ear: 8, hip: 24, knee: 26, ankle: 28, heel: 30, toe: 32 },
  left: { S: 11, E: 13, W: 15, pinky: 17, index: 19, ear: 7, hip: 23, knee: 25, ankle: 27, heel: 29, toe: 31 },
};
export const sidePoints = (side) => SIDES[side === 'left' ? 'left' : 'right'];

/* ======================================================================= *
 *  PARTS AND READINGS (section 1.6)                                        *
 *  curve: ['ramp', best, zero] | ['plateau', lo, hi, pad] | ['follow'] |   *
 *         ['code'] | ['onoff'].  abs: score |value|. trust per camera view *
 *  and for a far player. fine: needs the player near. timing: needs the   *
 *  frame rate. hand: needs the hand model.                                *
 * ======================================================================= */
const T = (front, side, back, angled, far) => ({ front, side, back, angled, far });
export const PARTS = [
  { id: 'fingers', label: 'Fingers', readings: [
    { key: 'spread', label: 'Fingers spread on the ball', curve: ['ramp', 1.9, 1.1], trust: T('low', 'medium', 'medium', 'low', 'none'), fine: true, hand: true, dp: 2, band: [1.0, 2.1] },
    { key: 'lead', label: 'Fingers that finish the shot', curve: ['code'], trust: T('low', 'medium', 'low', 'low', 'none'), fine: true, hand: true, dp: 0 },
    { key: 'point', label: 'Fingers pointing at the rim', curve: ['plateau', 0, 20, 50], trust: T('low', 'medium', 'low', 'low', 'none'), fine: true, hand: true, dp: 0, band: [0, 90] },
    { key: 'hold', label: 'Goose neck hold', curve: ['follow'], trust: T('medium', 'medium', 'medium', 'medium', 'none'), fine: true, timing: true, dp: 0, band: [0, 600] },
  ] },
  { id: 'wrist', label: 'Wrist', readings: [
    { key: 'cock', label: 'Wrist bent back at the set point', curve: ['plateau', 35, 75, 30], trust: T('low', 'high', 'low', 'medium', 'none'), fine: true, dp: 0, band: [0, 110] },
    { key: 'snap', label: 'Wrist snap', curve: ['ramp', 90, 20], trust: T('low', 'high', 'low', 'medium', 'none'), fine: true, timing: true, dp: 0, band: [0, 140] },
    { key: 'flex', label: 'Wrist bent down at the finish', curve: ['plateau', 50, 95, 40], trust: T('low', 'high', 'low', 'medium', 'none'), fine: true, dp: 0, band: [0, 130] },
  ] },
  { id: 'elbow', label: 'Elbow', readings: [
    { key: 'set', label: 'Elbow bend at the set point', curve: ['plateau', 75, 100, 30], trust: T('medium', 'high', 'medium', 'medium', 'medium'), dp: 0, weight: 0.15, band: [40, 140] },
    { key: 'align', label: 'Elbow under the ball', curve: ['ramp', 0.08, 0.45], abs: true, reuse: 'scoreElbow', trust: T('high', 'none', 'high', 'medium', 'medium'), dp: 3, weight: 0.35, band: [0, 0.6] },
    { key: 'flare', label: 'Elbow pointing out', curve: ['plateau', -15, 15, 30], trust: T('medium', 'low', 'medium', 'low', 'low'), dp: 0, weight: 0.10, band: [-45, 60] },
    { key: 'ext', label: 'Arm straight at release', curve: ['ramp', 175, 145], trust: T('medium', 'high', 'medium', 'high', 'medium'), dp: 0, weight: 0.15, band: [130, 180] },
    { key: 'rel', label: 'Release angle', curve: ['ramp', 0, 40], reuse: 'scoreRelease', trust: T('medium', 'high', 'medium', 'medium', 'medium'), dp: 0, weight: 0.25, band: [20, 100] },
  ] },
  { id: 'shoulders', label: 'Shoulders', readings: [
    { key: 'level', label: 'Shoulders level', curve: ['plateau', 0, 8, 20], abs: true, trust: T('high', 'none', 'high', 'medium', 'high'), dp: 0, band: [0, 30] },
    { key: 'square', label: 'Shoulders facing the rim', curve: ['ramp', 10, 45], trust: T('medium', 'none', 'medium', 'low', 'medium'), dp: 0, band: [0, 50] },
    { key: 'shrug', label: 'Relaxed shoulders', curve: ['ramp', 0.05, 0.25], trust: T('medium', 'none', 'low', 'low', 'none'), fine: true, dp: 2, band: [-0.05, 0.3] },
  ] },
  { id: 'head', label: 'Head', readings: [
    { key: 'still', label: 'Head still', curve: ['ramp', 0.05, 0.35], trust: T('high', 'medium', 'low', 'medium', 'none'), fine: true, dp: 2, band: [0, 0.4] },
    { key: 'rim', label: 'Head toward the rim', curve: ['onoff'], trust: T('medium', 'none', 'none', 'medium', 'none'), fine: true, dp: 0 },
  ] },
  { id: 'core', label: 'Core', readings: [
    { key: 'side', label: 'Leaning sideways', curve: ['ramp', 8, 38], abs: true, reuse: 'LEAN', trust: T('high', 'none', 'high', 'medium', 'high'), dp: 0, band: [0, 40] },
    { key: 'fwd', label: 'Leaning forward or back', curve: ['plateau', -5, 12, 25], trust: T('low', 'high', 'low', 'medium', 'high'), dp: 0, band: [-30, 40] },
  ] },
  { id: 'hips', label: 'Hips', readings: [
    { key: 'square', label: 'Hips facing the rim', curve: ['ramp', 10, 45], trust: T('medium', 'none', 'medium', 'low', 'medium'), dp: 0, band: [0, 50] },
    { key: 'dip', label: 'Hip dip', curve: ['plateau', 0.10, 0.30, 0.15], trust: T('high', 'high', 'high', 'high', 'high'), dp: 3, band: [-0.05, 0.5] },
    { key: 'driftSide', label: 'Drift sideways', curve: ['ramp', 0.05, 0.40], abs: true, trust: T('high', 'none', 'high', 'medium', 'high'), dp: 3, band: [0, 0.45] },
    { key: 'driftFwd', label: 'Drift forward or back', curve: ['plateau', -0.05, 0.30, 0.35], trust: T('low', 'high', 'low', 'medium', 'high'), dp: 3, band: [-0.45, 0.7] },
  ] },
  { id: 'knees', label: 'Knees', readings: [
    { key: 'bend', label: 'Knee bend', curve: ['plateau', 110, 140, 25], trust: T('medium', 'high', 'medium', 'high', 'medium'), dp: 0, band: [80, 180] },
    { key: 'track', label: 'Knees over toes', curve: ['ramp', 5, 25], trust: T('high', 'none', 'medium', 'low', 'medium'), dp: 0, band: [0, 35] },
    { key: 'sym', label: 'Both knees the same', curve: ['ramp', 5, 30], trust: T('medium', 'low', 'medium', 'low', 'low'), dp: 0, band: [0, 35] },
  ] },
  { id: 'ankles', label: 'Ankles', readings: [
    { key: 'drive', label: 'Ankle push', curve: ['ramp', 35, 5], trust: T('low', 'high', 'low', 'medium', 'low'), dp: 0, band: [0, 60] },
    { key: 'offTogether', label: 'Both feet leave together', curve: ['ramp', 30, 150], trust: T('high', 'medium', 'high', 'high', 'medium'), timing: true, dp: 0, band: [0, 200] },
    { key: 'landTogether', label: 'Both feet land together', curve: ['ramp', 30, 150], trust: T('high', 'medium', 'high', 'high', 'medium'), timing: true, dp: 0, band: [0, 200] },
  ] },
  { id: 'heels', label: 'Heels', readings: [
    { key: 'rise', label: 'Heels lift, then toes', curve: ['plateau', 30, 250, 100], trust: T('low', 'high', 'medium', 'medium', 'low'), timing: true, dp: 0, band: [-100, 380] },
    { key: 'land', label: 'Toes then heels on the landing', curve: ['plateau', 20, 200, 60], trust: T('low', 'high', 'medium', 'medium', 'low'), timing: true, dp: 0, band: [-80, 260] },
  ] },
  { id: 'toes', label: 'Toes and feet', readings: [
    { key: 'width', label: 'Feet width', curve: ['plateau', 0.8, 1.3, 0.5], trust: T('high', 'none', 'high', 'medium', 'high'), dp: 2, band: [0.3, 1.9] },
    { key: 'point', label: 'Toes pointing the same way', curve: ['ramp', 10, 45], trust: T('low', 'low', 'low', 'low', 'none'), dp: 0, band: [0, 60] },
    { key: 'spotSide', label: 'Landing spot, sideways', curve: ['ramp', 0.05, 0.40], abs: true, trust: T('high', 'none', 'high', 'medium', 'high'), dp: 3, band: [0, 0.45] },
    { key: 'spotFwd', label: 'Landing spot, forward or back', curve: ['plateau', -0.05, 0.30, 0.35], trust: T('low', 'high', 'low', 'medium', 'high'), dp: 3, band: [-0.45, 0.7] },
    { key: 'stick', label: 'Stick the landing', curve: ['ramp', 0.25, 1.15], trust: T('high', 'medium', 'high', 'medium', 'medium'), dp: 2, band: [0, 1.4] },
  ] },
];
export const PART_IDS = PARTS.map((p) => p.id);
const PART = Object.fromEntries(PARTS.map((p) => [p.id, p]));
const READING = Object.fromEntries(PARTS.flatMap((p) => p.readings.map((r) => [`${p.id}.${r.key}`, { ...r, part: p.id }])));
export const partById = (id) => PART[id] || null;
export const readingDef = (part, key) => READING[`${part}.${key}`] || null;

/* ======================================================================= *
 *  COPY. Every word the Body Map says. Kid words, short sentences, never   *
 *  shaming, never medical, no dashes. scripts/bodyparts-test.mjs checks    *
 *  every string. Drafts until Michael approves.                            *
 * ======================================================================= */
export const SAFETY = 'If something hurts, stop and tell a parent, coach or athletic trainer.';
export const COPY = {
  title: 'Your Body Map',
  sub: 'Every part of your shot, read from your body, one part at a time.',
  status: { good: 'Locked in', okay: 'Almost there', work: "Let's work on it", rough: 'Rough read', few: 'A few more shots', unseen: 'Not seen yet' },
  legend: [['good', 'Locked in'], ['okay', 'Almost there'], ['work', "Let's work on it"], ['unseen', 'Not seen yet']],
  sure: { high: 'very sure', mid: 'pretty sure', rough: 'rough read' },
  sureLine: 'How sure I am: {sure}',
  fromLine: 'Read from {n} of {total} shots. Camera: {view}.',
  viewWords: { front: 'front', back: 'behind you', side: 'side', angled: 'at an angle', mixed: 'a few spots' },
  roughRow: 'rough read',
  shared: {
    rough: 'Your {part}: I need a better look',
    few: 'Your {part}: a few more shots and I will know',
    unseen: 'Your {part}: not seen yet',
  },
  fewLine: 'A few more shots and I will know.',
  reasons: {
    'no-hand': 'I could not see your fingers from here. Move closer, or turn the camera to your shooting side.',
    'hand-small': 'Your hand looks tiny from here. Step closer so I can see your fingers.',
    'fingers-hidden': 'I could not see your fingers spread on this one. The ball is often in the way from the front. Put the phone behind you, looking toward the rim, and I can see them.',
    'palm-edge': 'Your hand was turned sideways to me, so your fingers were stacked up. Put the phone behind you, looking toward the rim, and I can see them spread.',
    'needs-side': 'I need to see you from the side for this one. Put the phone at your shooting side, far enough that you fit from head to toes.',
    'needs-front': 'I need to see you from the front for this one. Put the phone under the hoop, facing you.',
    'too-far': 'You are a little far for this one. Step closer, or move the phone closer to you.',
    'low-fps': 'The camera was running slow, so quick moves blur for me. Close other apps and try again.',
    'joint-hidden': 'Part of your {part} was out of the picture. Make sure you fit from head to toes.',
    'no-jump': 'That was a shot with no jump, so there is nothing to read here. That is totally fine.',
    'no-rim': 'Tap the rim on the camera screen, or tell me your phone is under the hoop, and I can check this.',
    'head-square': 'Your head faced the camera, so I could not tell where the rim was.',
    'not-landed': 'I missed your landing. Keep your feet in the picture after the shot.',
  },
  parts: {
    fingers: {
      good: 'Your fingers: soft touch', okay: 'Your fingers: almost there',
      note: 'I cannot see the ball leave your hand. I look at where your fingers point right after.',
      readings: {
        spread: { measured: 'Your fingertips spread {v} percent wider than your knuckles at the set point. We look for about 70 percent or more.', work: 'Your fingers: spread them on the ball', tip: 'Spread your fingers wide and let the ball sit on your finger pads.' },
        lead: { measured: 'Right after your release, your {finger} pointed down the line.', work: 'Your fingers: finish with the middle ones', tip: 'Let the ball roll off your pointer and middle fingers last.' },
        point: { measured: 'Your fingers ended {v} degrees off the line to the rim.', work: 'Your fingers want to point at the rim', tip: 'After the shot, reach into the rim like you are grabbing a cookie from a high jar.' },
        hold: { measured: 'You held your goose neck for {v} seconds.', measuredArm: 'You held your arm up for {v} seconds. I could not see your fingers, so this is your arm hold.', work: 'Your fingers: hold that goose neck', tip: 'Freeze your hand after the shot until the ball hits the rim or the net.' },
      },
    },
    wrist: {
      good: 'Your wrist: wet', okay: 'Your wrist: getting there',
      readings: {
        cock: { measured: 'Your wrist bent back {v} degrees before you shot.', work: 'Your wrist: load it back', tip: 'Bend your wrist back like you are holding a tray, then shoot.' },
        snap: { measured: 'Your wrist snapped through {v} degrees.', work: 'Your wrist wants a bigger snap', tip: 'Snap your wrist down like you are waving goodbye to the ball.' },
        flex: { measured: 'Your wrist finished bent down {v} degrees.', work: 'Your wrist: finish bent down', tip: 'Finish with your wrist relaxed and your fingers toward the floor.' },
      },
    },
    elbow: {
      good: 'Your elbow: locked in', okay: 'Your elbow: close',
      readings: {
        set: { measured: 'Your elbow bent to {v} degrees at the set point. We look for about 75 to 100.', work: 'Your elbow: find your L', tip: 'Make an L with your shooting arm before you go up.' },
        align: { measured: 'Your elbow sat {v} percent of a shoulder width to the side of your wrist.', work: 'Your elbow: tuck it under the ball', tip: 'Keep your elbow under the ball so the ball, elbow and rim line up.' },
        flare: { measured: 'Your elbow pointed out about {v} degrees.', work: 'Your elbow: point it at the rim', tip: 'Point your elbow at the rim, not out to the side.' },
        ext: { measured: 'Your arm opened to {v} degrees when you let go. 180 is all the way straight.', work: 'Your elbow: reach all the way up', tip: 'Reach up and out like you are putting the ball on a high shelf.' },
        rel: { measured: 'Your forearm pointed {v} degrees up when you let go. We aim for about 62.', workLow: 'Your release: send it higher', workHigh: 'Your release: push it out more', tipLow: 'Shoot up and over the front of the rim, like a rainbow.', tipHigh: 'Push the ball out toward the rim, not just straight up.' },
      },
    },
    shoulders: {
      good: 'Your shoulders: square and steady', okay: 'Your shoulders: nearly there',
      readings: {
        level: { measured: 'Your shoulders tipped {v} degrees at the set point, {side} side higher.', work: 'Your shoulders: level them out', tip: 'Keep both shoulders at the same height, like a table top.' },
        square: { measured: 'Your shoulders turned {v} degrees from the rim. A small turn is normal for lots of shooters.', work: 'Your shoulders: face the rim more', tip: 'Point your chest at the rim before the catch.' },
        shrug: { measured: 'Your guide shoulder rose {v} percent of a shoulder width.', work: 'Your shoulders: stay relaxed', tip: 'Breathe out and let your shoulders drop before you shoot.' },
      },
    },
    head: {
      good: 'Your head: locked on', okay: 'Your head: pretty steady',
      note: 'I read which way your head points, not your eyes.',
      readings: {
        still: { measured: 'Your head moved {v} percent of a shoulder width during the shot.', work: 'Your head: keep it still', tip: 'Keep your head still like a camera on a tripod.' },
        rim: { measured: 'Your head was turned {dir} the rim when you let go. I read your head, not your eyes.', work: 'Your head: turn it to the rim', tip: 'Find the front of the rim early and keep looking at it.' },
      },
    },
    core: {
      good: 'Your core: tall and steady', okay: 'Your core: nearly straight',
      readings: {
        side: { measured: 'You leaned {v} degrees to your {dir} when you let go.', work: 'Your core: stay straight up', tip: 'Jump straight up, like you are inside a tall tube.' },
        fwd: { measured: 'You leaned {v} degrees {dir} when you let go.', work: 'Your core: stay tall', tip: 'Keep your chest tall and jump up, not back.' },
      },
    },
    hips: {
      good: 'Your hips: centered', okay: 'Your hips: close',
      readings: {
        square: { measured: 'Your hips turned {v} degrees from the rim.', work: 'Your hips: face the rim', tip: 'Point your belly button at the rim.' },
        dip: { measured: 'You sank down {v} percent of your torso before you went up.', workLow: 'Your hips: sit into it a little more', workHigh: 'Your hips: a smaller dip is plenty', tipLow: 'Bend like you are about to sit on a tall stool, then pop up.', tipHigh: 'A quick small dip is all you need.' },
        driftSide: { measured: 'You landed {v} percent of your torso to the {dir} of where you took off.', work: 'Your hips: land where you took off', tip: 'Take off and land on the same spot. Try it on a court line.' },
        driftFwd: { measured: 'You landed {v} percent of your torso {dir} where you took off.', workHigh: 'Your hips: land closer to your take off', workLow: 'Your hips: jump straight up', tip: 'Jump up, not back. Land right where you started or a little forward.' },
      },
    },
    knees: {
      good: 'Your knees: spring loaded', okay: 'Your knees: almost',
      readings: {
        bend: { measured: 'Your knees bent to {v} degrees. Straight legs are 180.', workHigh: 'Your knees: bend a little more', workLow: 'Your knees: not so low', tipHigh: 'Bend your knees like a spring before you go up.', tipLow: 'A small, quick bend gives you more pop.' },
        track: { measured: 'Your knees moved {v} degrees in from your toes.', work: 'Your knees: point them over your toes', tip: 'Push your knees out so they point the same way as your toes.' },
        sym: { measured: 'One knee bent {v} degrees more than the other.', work: 'Your knees: bend both the same', tip: 'Load both legs evenly, like each foot is on its own scale.' },
      },
    },
    ankles: {
      good: 'Your ankles: pop', okay: 'Your ankles: getting there',
      readings: {
        drive: { measured: 'Your ankles pushed open {v} degrees as you left the floor.', work: 'Your ankles: push off the floor', tip: 'Push through the balls of your feet, like a jump rope hop.' },
        offTogether: { measured: 'Your feet left the floor {v} seconds apart.', work: 'Your ankles: take off together', tip: 'Hop off both feet at the same time.' },
        landTogether: { measured: 'Your feet landed {v} seconds apart.', work: 'Your ankles: land together', tip: 'Land on both feet at once, soft like a cat.' },
      },
    },
    heels: {
      good: 'Your heels: smooth roll', okay: 'Your heels: close',
      readings: {
        rise: { measured: 'Your heels came up {v} seconds before your toes left.', measuredEarly: 'You were up on your toes before the dip. Your heels came up {v} seconds before your toes left.', work: 'Your heels: roll up to your toes', tip: 'Roll from your heels to your toes as you go up.' },
        land: { measured: 'Your toes touched {v} seconds before your heels.', measuredNeg: 'Your heels touched first, {v} seconds before your toes.', work: 'Your heels: land toes first', tip: 'Land on the balls of your feet, then let your heels come down.' },
      },
    },
    toes: {
      good: 'Your feet: quiet feet', okay: 'Your feet: nearly set',
      readings: {
        width: { measured: 'Your feet were {v} shoulder widths apart.', workLow: 'Your feet: a bit wider', workHigh: 'Your feet: a bit closer', tip: 'Set your feet about as wide as your shoulders.' },
        point: { measured: 'Your feet pointed {v} degrees apart. Feet direction is hard to read from one camera. This one is a rough guess.', work: 'Your feet: point them the same way', tip: 'Point both feet the same way, toward the rim.' },
        spotSide: { measured: 'You landed {v} percent of your torso to the {dir} of your take off spot.', work: 'Your feet: land where you took off', tip: 'Take off and land on the same spot.' },
        spotFwd: { measured: 'You landed {v} percent of your torso {dir} your take off spot.', work: 'Your feet: land where you took off', tip: 'Take off and land on the same spot.' },
        stick: { measured: 'You stuck it.', measuredSteps: 'You took {n} extra steps after landing.', measuredStep: 'You took 1 extra step after landing.', work: 'Your feet: freeze on the landing', tip: 'Land and freeze for a count of two.' },
      },
    },
  },
  fingerNames: ['pointer and middle fingers', 'pointer finger', 'middle finger', 'ring finger', 'pinky'],
  dirs: { left: 'left', right: 'right', forward: 'forward', back: 'back', ahead: 'ahead of', behind: 'behind', toward: 'toward', away: 'away from' },
  drills: {
    fingers: { name: 'Lay down flicks', how: 'Lie on your back and shoot the ball straight up off your fingertips, then catch it. 20 flicks. Finish with your fingers pointing at the ball.', skill: 'shooting', draft: true },
    wrist: { name: 'One hand form shooting', how: 'Six feet out, guide hand off the ball, ten makes before you step back. Hold the goose neck every time.', skill: 'shooting', draft: false },
    elbow: { name: 'Line up', how: 'Stand on a straight court line facing the rim. Shoot one hand form shots with the ball, your elbow and the line stacked. 10 makes.', skill: 'shooting', draft: true },
    shoulders: { name: 'Mirror set point', how: 'Face the camera or a mirror. Bring the ball to your set point 10 times, shoulders level and facing the target. No shot needed.', skill: 'shooting', draft: true },
    head: { name: 'Eyes on target', how: 'Pick the front of the rim. Keep your head still and look at it until the ball lands. 10 close shots.', skill: 'shooting', draft: true },
    core: { name: 'Tall tube', how: 'Close form shots. Jump like you are inside a tall tube, up and down, no leaning. 10 makes.', skill: 'shooting', draft: true },
    hips: { name: 'Line jumps', how: 'Both feet on a court line. Shoot close form shots and land back on the line. 10 makes.', skill: 'shooting', draft: true },
    knees: { name: 'Knees over toes dips', how: 'No ball: 10 slow dips with your knees pointing the same way as your toes. Then 10 close shots the same way.', skill: 'shooting', draft: true },
    ankles: { name: 'Two foot pop', how: '10 small hops off both feet at the same time, landing soft. Then 10 close shots the same way.', skill: 'shooting', draft: true },
    heels: { name: 'Rock and rise', how: 'Rock slowly from heels to toes 10 times. Then 10 close shots rolling up through your toes.', skill: 'shooting', draft: true },
    toes: { name: 'Feet first catch and shoot', how: 'Pass off a wall or a partner. Feet set before the catch, up on the catch.', skill: 'shooting', draft: false },
  },
  badges: {
    'soft-touch': { part: 'fingers', name: 'Soft Touch', tier: 'bronze', blurb: 'Ten shots with fingers pointing home. Soft.' },
    'wet-wrist': { part: 'wrist', name: 'Wet Wrist', tier: 'bronze', blurb: 'Ten snaps in one session. That wrist is wet.' },
    'laser-elbow': { part: 'elbow', name: 'Laser Elbow', tier: 'bronze', blurb: 'Ten shots with the elbow lined up like a laser.' },
    'square-up': { part: 'shoulders', name: 'Square Up', tier: 'bronze', blurb: 'Ten shots square and steady up top.' },
    'locked-on': { part: 'head', name: 'Locked On', tier: 'bronze', blurb: 'Ten shots with your head still and on target.' },
    'tall-and-steady': { part: 'core', name: 'Tall And Steady', tier: 'bronze', blurb: 'Ten shots straight up, no lean.' },
    'launch-pad': { part: 'hips', name: 'Launch Pad', tier: 'bronze', blurb: 'Ten shots landing right where you launched.' },
    'spring-loaded': { part: 'knees', name: 'Spring Loaded', tier: 'bronze', blurb: 'Ten shots with a smooth, even knee bend.' },
    'pop-off': { part: 'ankles', name: 'Pop Off', tier: 'bronze', blurb: 'Ten two foot take offs in one session.' },
    'roll-through': { part: 'heels', name: 'Roll Through', tier: 'bronze', blurb: 'Ten shots rolling heel to toe.' },
    'quiet-feet': { part: 'toes', name: 'Quiet Feet', tier: 'bronze', blurb: 'Ten shots with quiet, set feet.' },
    'glow-up': { part: null, name: 'Glow Up', tier: 'silver', blurb: 'A part went from Let\'s work on it to Locked in. That is real growth.' },
    'head-to-toe': { part: null, name: 'Head To Toe', tier: 'gold', blurb: 'Every part locked in during one session. Head to toe.' },
  },
  badgeLocked: 'Ten good shots for your {part} in one session unlocks it. Best so far: {best}.',
  xpLine: '+{xp} XP from your {part}',
  level: 'Body level {n}',
  toNext: '{n} XP to the next level',
  streak: '{n} day body streak',
  streakOne: '1 day body streak',
  streakNone: 'Start a streak today.',
  best: 'Best part {when}: {part}.',
  next: 'Next up: {part}.',
  bestWhen: { today: 'today', lately: 'lately' },
  noData: 'Shoot a few on camera and your body map lights up here.',
  celebrate: 'Your {part} went from {from} to {to}!',
  celebrateXp: '+{xp} XP',
  footnote: 'I read this from your body, not the ball. From one camera, depth is my best guess.',
  rough: SRC.ROUGH_LINE,
  measuredTitle: 'What I measured',
  replayTitle: 'Your shot, frame by frame',
  trendTitle: 'Your trend',
  drillTitle: 'Try this drill',
  drillButton: 'Try this drill',
  draftTag: 'New drill',
  badgesTitle: 'Badges and XP',
  trendEmpty: 'Your trend shows up after two sessions.',
  trendAria: '{part} score over your last {n} sessions: {list}. {dir}',
  trendDir: { up: 'Going up.', down: 'Going down a little. That happens. Keep at it.', flat: 'Holding steady.' },
  replay: {
    moments: { load: 'Dip', set: 'Set point', takeoff: 'Lift off', release: 'Release (arm straight)', follow: 'Follow through', land: 'Landing' },
    ticks: { load: 'Dip', set: 'Set', takeoff: 'Lift off', release: 'Release', follow: 'Follow', land: 'Land' },
    scrub: 'Key moment',
    play: 'Play', pause: 'Pause',
    last: 'Last shot', best: 'Best shot',
    turnLeft: 'Turn left', turnRight: 'Turn right',
    camera: 'Camera view', side: 'Side', top: 'Top',
    depth: 'Depth is my best guess',
    flat: 'Flat view. This shot has no depth.',
    ideal: 'Ideal line shown in blue.',
    aria: 'Your skeleton at the {moment}. {detail} Ideal line shown in blue.',
    none: 'No shot to replay yet for this part.',
    hand: 'Flat view of your hand',
    noHand: 'I could not see your fingers from here. Move closer, or turn the camera to your shooting side.',
    handGhost: 'The faint hand is the goose neck finish to aim for.',
    drag: 'Drag to turn your skeleton.',
  },
  modes: { shoot: 'Shooting', dribble: 'Dribbling', footwork: 'Footwork' },
  modeNote: {
    dribble: 'I watch your hand, not the ball. Dribble on camera and your hands, hips and knees show up here.',
    footwork: 'Stance, slides, pivots and first steps light up your knees, hips, ankles and feet.',
  },
  notInMode: 'Not read in this mode',
  pick: { last: 'Last session', lately: 'Last 3 sessions' },
  setup: {
    title: 'Where is your phone when you shoot?',
    rim: 'Under the hoop, facing me',
    side: 'At my shooting side',
    other: 'Somewhere else',
    front: 'Phone under the hoop, facing you, unlocks: elbow line, shoulders, knees over toes, feet width, drifting sideways and leaning sideways.',
    sideUnlocks: 'Phone at your shooting side unlocks: wrist snap, fingers, release angle, leaning forward or back, heels, ankle push and landing forward or back.',
    upright: 'Stand the phone up tall so your whole body fits with room above your hands.',
  },
  close: 'Close',
  openPart: 'Open {part}',
  partAria: '{part}: {status}.',
  scoreWord: 'score {n}',
  // the screen itself (public/js/bodymap.js)
  loading: 'Loading your body map.',
  loadError: 'I could not load your body map right now. Try again in a moment.',
  figureAria: 'Your body. Pick a part to see how it did.',
  handBubble: 'Hand',
  footBubble: 'Feet',
  openHint: 'Tap a part to see how it did.',
  partsTitle: 'Every part',
  modeLabel: 'What to show',
  rangeLabel: 'Show',
  tags: { best: 'Best', next: 'Next up' },
  avgOf: 'Average of {n} shots.',
  lastShotRow: 'From your last shot.',
  stickSome: 'You took an extra step after {x} of {n} landings.',
  earlySome: 'You were up on your toes before the dip on {x} of {n} shots.',
  xpAria: 'Body level {n}. {x} XP to the next level.',
  streakAria: '{n} day body streak',
  streakUnit: { one: 'day', many: 'days' },
  legendAria: 'What the colors mean',
  tryThis: 'Try this:',
  repHeadline: 'Your {part}: {status}',
  repLine: 'From {n} reps.',
  roughGuide: 'Rough guide',
  badgeNew: 'New badge: {name}!',
  badgesNew: '{n} new badges!',
  badgesMore: '{names} and {n} more. See them all below.',
  badgeEarned: 'Earned',
  badgeLockedTag: 'Locked',
  badgeProgress: '{n} of {m} good shots in one session',
  partBadgeTitle: 'Badge for this part',
  replayTitleRep: 'Replays are for shots for now.',
  replayControls: {
    shotPick: 'Which shot', views: 'Turn your skeleton', moment: 'Key moment: {moment}',
    elbowAt: 'Elbow bent {v} degrees.', kneesAt: 'Knees bent {v} degrees.',
  },
};

/* ======================================================================= *
 *  MATHS                                                                   *
 * ======================================================================= */
const DEG = 180 / Math.PI;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const fin = Number.isFinite;
/** scoring.js ramp(), copied: `best` scores 100, `zero` scores 0, either direction. */
export function ramp(value, best, zero) {
  if (zero === best) return 100;
  return Math.round(clamp(100 * (1 - (value - best) / (zero - best)), 0, 100));
}
/** scoring.js plateau(), copied: 100 inside [lo, hi], 0 at `pad` outside. */
export function plateau(value, lo, hi, pad) {
  if (value >= lo && value <= hi) return 100;
  const out = value < lo ? lo - value : value - hi;
  return ramp(out, 0, Math.max(pad, 1e-6));
}
/** 1.2 */
export function statusOf(score) {
  if (!fin(score)) return null;
  return score >= BODY.GOOD ? 'good' : score >= BODY.OKAY ? 'okay' : 'work';
}
const tierOf = (s) => STATUSES.indexOf(s);

/** Interior angle at b, degrees, 2D. The same maths as scoring.js jointAngle(). */
export function angle2(a, b, c) {
  const abx = a.x - b.x, aby = a.y - b.y, cbx = c.x - b.x, cby = c.y - b.y;
  const mag = Math.hypot(abx, aby) * Math.hypot(cbx, cby);
  if (mag === 0) return 0;
  return Math.acos(clamp((abx * cbx + aby * cby) / mag, -1, 1)) * DEG;
}
/** Interior angle at b, degrees, 3D. A copy of scoring.js jointAngle3() (not exported there). */
export function angle3(a, b, c) {
  const abx = a.x - b.x, aby = a.y - b.y, abz = (a.z || 0) - (b.z || 0);
  const cbx = c.x - b.x, cby = c.y - b.y, cbz = (c.z || 0) - (b.z || 0);
  const mag = Math.hypot(abx, aby, abz) * Math.hypot(cbx, cby, cbz);
  if (mag === 0) return 0;
  return Math.acos(clamp((abx * cbx + aby * cby + abz * cbz) / mag, -1, 1)) * DEG;
}
const sub3 = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: (a.z || 0) - (b.z || 0) });
const dot3 = (a, b) => a.x * b.x + a.y * b.y + (a.z || 0) * (b.z || 0);
const len3 = (a) => Math.hypot(a.x, a.y, a.z || 0);
const norm3 = (a) => { const l = len3(a) || 1e-9; return { x: a.x / l, y: a.y / l, z: (a.z || 0) / l }; };
const cross3 = (a, b) => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: ((a.z || 0) + (b.z || 0)) / 2 });
const dist2 = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const vecAngle2 = (u, v) => { const m = Math.hypot(u.x, u.y) * Math.hypot(v.x, v.y); return m ? Math.acos(clamp((u.x * v.x + u.y * v.y) / m, -1, 1)) * DEG : 0; };
const vecAngle3 = (u, v) => { const m = len3(u) * len3(v); return m ? Math.acos(clamp(dot3(u, v) / m, -1, 1)) * DEG : 0; };
function percentile(values, p) {
  const s = values.filter(fin).sort((a, b) => a - b);
  if (!s.length) return null;
  const i = clamp((s.length - 1) * p, 0, s.length - 1), lo = Math.floor(i), hi = Math.ceil(i);
  return lo === hi ? s[lo] : s[lo] + (s[hi] - s[lo]) * (i - lo);
}
const median = (v) => percentile(v, 0.5);
const mean = (v) => { const f = v.filter(fin); return f.length ? f.reduce((a, b) => a + b, 0) / f.length : null; };
const round = (v, dp = 0) => { const k = 10 ** dp; return Math.round(v * k) / k; };

/** 1.4: visibility to a 0..1 term. */
export function visTerm(v) {
  if (!(v >= BODY.VIS_LO)) return 0;
  if (v >= BODY.VIS_HI) return 1;
  return 0.5 + ((v - BODY.VIS_LO) / (BODY.VIS_HI - BODY.VIS_LO)) * 0.5;
}

/* ======================================================================= *
 *  FRAMES                                                                  *
 *  Inside, a frame is { t, p, w, hL, hR, sL, sR }: p is 33 x (x, y, vis)   *
 *  in frame units, w is 33 x (X, Y, Z) metres or null, hL/hR are 21 x      *
 *  (x, y, z) for each hand or null, sL/sR the hand scores.                 *
 * ======================================================================= */
/** One frame from what the page has: smoothed landmarks with lm.world, and [{ side, lm, score }] hands. */
export function toFrame(t, lm, hands) {
  const f = { t, p: new Float32Array(99), w: null, hL: null, hR: null, sL: null, sR: null };
  fillPose(f.p, lm);
  const w = lm?.world;
  if (Array.isArray(w) && w.length >= 33 && fin(w[23]?.x)) { f.w = new Float32Array(99); fillWorld(f.w, w); }
  for (const h of hands || []) {
    if (!h || !Array.isArray(h.lm) || h.lm.length < 21 || (h.side !== 'left' && h.side !== 'right')) continue;
    const a = new Float32Array(63); fillHand(a, h.lm);
    if (h.side === 'left') { f.hL = a; f.sL = fin(h.score) ? h.score : null; } else { f.hR = a; f.sR = fin(h.score) ? h.score : null; }
  }
  return f;
}
function fillPose(out, lm, o = 0) {
  for (let i = 0; i < 33; i++) {
    const p = lm?.[i];
    const ok = p && fin(p.x) && fin(p.y);
    out[o + i * 3] = ok ? p.x : NaN; out[o + i * 3 + 1] = ok ? p.y : NaN;
    out[o + i * 3 + 2] = ok ? (p.visibility === undefined ? 1 : fin(p.visibility) ? p.visibility : 0) : 0;
  }
}
function fillWorld(out, w, o = 0) {
  for (let i = 0; i < 33; i++) { const p = w[i]; out[o + i * 3] = p?.x ?? NaN; out[o + i * 3 + 1] = p?.y ?? NaN; out[o + i * 3 + 2] = p?.z ?? NaN; }
}
function fillHand(out, h, o = 0) {
  for (let j = 0; j < 21; j++) { const p = h[j]; out[o + j * 3] = p?.x ?? NaN; out[o + j * 3 + 1] = p?.y ?? NaN; out[o + j * 3 + 2] = fin(p?.z) ? p.z : 0; }
}

/** Geometry on one frame, in square units (one unit = one frame height). */
function geom(aspect) {
  const P = (f, i) => (f && fin(f.p[i * 3]) ? { x: f.p[i * 3] * aspect, y: f.p[i * 3 + 1], v: f.p[i * 3 + 2] } : null);
  const Q = (f, i) => (f && f.w && fin(f.w[i * 3]) ? { x: f.w[i * 3], y: f.w[i * 3 + 1], z: f.w[i * 3 + 2] } : null);
  const H = (f, j) => (f && f.h ? { x: f.h[j * 3] * aspect, y: f.h[j * 3 + 1], z: f.h[j * 3 + 2] } : null);
  const vis = (f, ...idx) => (f ? Math.min(...idx.map((i) => (fin(f.p[i * 3]) ? f.p[i * 3 + 2] : 0))) : 0);
  const sw = (f) => { const a = P(f, 11), b = P(f, 12); return a && b ? dist2(a, b) : null; };
  const sMid = (f) => { const a = P(f, 11), b = P(f, 12); return a && b ? mid(a, b) : null; };
  const hMid = (f) => { const a = P(f, 23), b = P(f, 24); return a && b ? mid(a, b) : null; };
  const aMid = (f) => { const a = P(f, 27), b = P(f, 28); return a && b ? mid(a, b) : null; };
  const bodyPx = (f) => { const s = sMid(f), h = hMid(f), w = sw(f); return s && h && w !== null ? Math.max(dist2(s, h), 0.6 * w, 1e-4) : null; };
  /** Shoulder width as a unit that holds from any camera spot. Side on, the
   *  shoulders overlap in the picture and their width falls toward zero, which
   *  would blow up anything divided by it; a square on player's shoulders are
   *  about Q0 torsos wide, so that stands in. */
  const swEff = (f) => { const w = sw(f), s = sMid(f), h = hMid(f); return w === null ? null : Math.max(w, s && h ? BODY.Q0 * dist2(s, h) : 0, 1e-4); };
  /** World body frame: up, the player's right, and where the chest faces. */
  const frameW = (f) => {
    const l = Q(f, 11), r = Q(f, 12), lh = Q(f, 23), rh = Q(f, 24);
    if (!l || !r || !lh || !rh) return null;
    const u = norm3(sub3(mid(l, r), mid(lh, rh)));
    let rv = sub3(r, l); rv = norm3(sub3(rv, { x: u.x * dot3(rv, u), y: u.y * dot3(rv, u), z: u.z * dot3(rv, u) }));
    const fw = norm3(cross3(u, rv));
    return { u, r: rv, f: fw };
  };
  return { P, Q, H, vis, sw, swEff, sMid, hMid, aMid, bodyPx, frameW };
}

/* ======================================================================= *
 *  1.5 CAMERA VIEW                                                         *
 * ======================================================================= */
export function detectView(frame, aspect, q0 = BODY.Q0) {
  const g = geom(aspect);
  const sw = g.sw(frame), bp = g.bodyPx(frame);
  if (sw === null || bp === null) return null;
  const q = sw / bp;
  const yawI = Math.acos(clamp(q / q0, 0, 1)) * DEG;
  const l = g.Q(frame, 11), r = g.Q(frame, 12);
  const yawW = l && r ? Math.atan2(Math.abs(r.z - l.z), Math.abs(r.x - l.x)) * DEG : null;
  const yaw = yawW !== null ? BODY.YAW_WORLD_W * yawW + (1 - BODY.YAW_WORLD_W) * yawI : yawI;
  const p11 = g.P(frame, 11), p12 = g.P(frame, 12);
  const facingCamera = p11.x > p12.x;
  const view = yaw <= BODY.FRONT_MAX ? (facingCamera ? 'front' : 'back') : yaw <= BODY.ANGLED_MAX ? 'angled' : 'side';
  // Which way the player faces across the picture: +1 image right. From the
  // chest direction when the world points give one, else nose ahead of the ears.
  let facing = 0;
  const fw = g.frameW(frame);
  if (fw && Math.abs(fw.f.x) >= 0.35) facing = Math.sign(fw.f.x);
  else {
    const n = g.P(frame, 0), e7 = g.P(frame, 7), e8 = g.P(frame, 8);
    if (n && e7 && e8 && Math.abs(n.x - (e7.x + e8.x) / 2) > 0.15 * sw) facing = Math.sign(n.x - (e7.x + e8.x) / 2);
  }
  // Shoulder line direction on the floor, for "the body turned mid shot".
  const heading = l && r ? Math.atan2(r.z - l.z, r.x - l.x) * DEG : null;
  return { view, yaw, yawW, yawI, q, facingCamera, facing, heading };
}

/* ======================================================================= *
 *  1.3 KEY FRAMES                                                          *
 * ======================================================================= */
/** A frame with the shooting hand picked out: { t, p, w, h, hs }. */
function shotFrame(f, side) {
  return { t: f.t, p: f.p, w: f.w, h: side === 'left' ? f.hL : f.hR, hs: side === 'left' ? f.sL : f.sR };
}
/** Per coordinate median of a frame and its neighbours within MEDIAN_MS. */
function medianFrame(fr, i) {
  const c0 = fr[i];
  const nb0 = [fr[i - 1], c0, fr[i + 1]].filter((f) => f && Math.abs(f.t - c0.t) <= BODY.MEDIAN_MS);
  if (nb0.length < 2) return { ...c0 };
  // A slow phone runs the hand model on one frame in a few (trackmath.js
  // createCadence), so a key frame without a hand takes its nearest
  // neighbour's, never more than MEDIAN_MS away: the same moment the median
  // already treats as one.
  const hn = c0.h ? null : nb0.filter((f) => f !== c0 && f.h).sort((a, b) => Math.abs(a.t - c0.t) - Math.abs(b.t - c0.t))[0];
  const c = hn ? { ...c0, h: hn.h, hs: hn.hs } : c0;
  const nb = nb0.map((f) => (f === c0 ? c : f));
  const med = (arrs, n) => {
    const out = new Float32Array(n);
    for (let k = 0; k < n; k++) out[k] = median(arrs.map((a) => a[k])) ?? NaN;
    return out;
  };
  const withW = nb.filter((f) => f.w), withH = nb.filter((f) => f.h);
  return {
    t: c.t, p: med(nb.map((f) => f.p), 99),
    w: c.w && withW.length >= 2 ? med(withW.map((f) => f.w), 99) : c.w,
    h: c.h && withH.length >= 2 ? med(withH.map((f) => f.h), 63) : c.h,
    hs: c.hs,
  };
}
/**
 * The extreme of a series over candidate frames: a short centred average finds
 * it, then a parabola through the raw values around it places it, so a flat
 * bottom and a little jitter do not move the pick by several frames.
 */
function extremeIndex(fr, idxs, valueOf, wantMax, gap) {
  const pts = idxs.map((i) => ({ i, t: fr[i].t, v: valueOf(fr[i]) })).filter((p) => fin(p.v));
  if (!pts.length) return null;
  if (pts.length < 3) return pts.reduce((a, b) => ((wantMax ? b.v > a.v : b.v < a.v) ? b : a)).i;
  const half = BODY.EXT_SMOOTH_MS;
  const sm = pts.map((p) => mean(pts.filter((q) => Math.abs(q.t - p.t) <= half).map((q) => q.v)));
  let k = 0;
  for (let j = 1; j < pts.length; j++) if (wantMax ? sm[j] > sm[k] : sm[j] < sm[k]) k = j;
  const W = Math.max(2.2 * gap, BODY.EXT_FIT_MS);
  const near = pts.filter((q) => Math.abs(q.t - pts[k].t) <= W);
  if (near.length >= 3) {
    // least squares y = a t^2 + b t + c, t centred on the pick
    const t0 = pts[k].t;
    let s0 = 0, s1 = 0, s2 = 0, s3 = 0, s4 = 0, y0 = 0, y1 = 0, y2 = 0;
    for (const q of near) { const x = q.t - t0; s0++; s1 += x; s2 += x * x; s3 += x * x * x; s4 += x * x * x * x; y0 += q.v; y1 += x * q.v; y2 += x * x * q.v; }
    const det = s4 * (s2 * s0 - s1 * s1) - s3 * (s3 * s0 - s1 * s2) + s2 * (s3 * s1 - s2 * s2);
    if (Math.abs(det) > 1e-9) {
      const a = (y2 * (s2 * s0 - s1 * s1) - s3 * (y1 * s0 - s1 * y0) + s2 * (y1 * s1 - s2 * y0)) / det;
      const b = (s4 * (y1 * s0 - y0 * s1) - y2 * (s3 * s0 - s1 * s2) + s2 * (s3 * y0 - y1 * s2)) / det;
      if ((wantMax && a < 0) || (!wantMax && a > 0)) {
        const tv = clamp(t0 - b / (2 * a), near[0].t, near[near.length - 1].t);
        return near.reduce((p, q) => (Math.abs(q.t - tv) < Math.abs(p.t - tv) ? q : p)).i;
      }
    }
  }
  return pts[k].i;
}
const nearestIndex = (fr, t) => {
  let best = -1, bd = Infinity;
  for (let i = 0; i < fr.length; i++) { const d = Math.abs(fr[i].t - t); if (d < bd) { bd = d; best = i; } }
  return best;
};

/**
 * Pick the six key moments (section 1.3) from the frames around one shot.
 * `frames` are toFrame() frames, oldest first. Returns null when there is no
 * release frame to anchor on.
 */
export function pickKeyframes(frames, shot, { aspect = 1, standY = null } = {}) {
  if (!Array.isArray(frames) || frames.length < 3 || !shot || !fin(shot.releasedAt)) return null;
  const side = shot.side === 'left' ? 'left' : 'right';
  const fr = frames.map((f) => shotFrame(f, side));
  const gaps = []; for (let i = 1; i < fr.length; i++) gaps.push(fr[i].t - fr[i - 1].t);
  const gap = median(gaps.filter((g) => g > 0)) || 33;
  const fps = 1000 / gap;
  const g = geom(aspect), J = SIDES[side];
  const iRel = nearestIndex(fr, shot.releasedAt);
  if (iRel < 0 || Math.abs(fr[iRel].t - shot.releasedAt) > Math.max(gap, 50)) return null;
  const tRel = fr[iRel].t;
  const inWin = (a, b) => { const out = []; for (let i = 0; i < fr.length; i++) if (fr[i].t >= a && fr[i].t <= b) out.push(i); return out; };
  const elbowAt = (f) => {
    if (g.vis(f, J.S, J.E, J.W) < BODY.VIS_LO) return null;
    const qs = g.Q(f, J.S), qe = g.Q(f, J.E), qw = g.Q(f, J.W);
    return qs && qe && qw ? angle3(qs, qe, qw) : angle2(g.P(f, J.S), g.P(f, J.E), g.P(f, J.W));
  };
  const riseAt = (f) => { const s = g.P(f, J.S), w = g.P(f, J.W), d = g.swEff(f); return s && w && d ? (s.y - w.y) / d : null; };

  // set: smallest elbow angle with the wrist at or above the shoulder
  let iSet = extremeIndex(fr, inWin(tRel - BODY.SET_BACK_MS, tRel - 1).filter((i) => riseAt(fr[i]) >= 0), elbowAt, false, gap);
  if (iSet === null) iSet = extremeIndex(fr, inWin(tRel - BODY.SET_FALLBACK_MS, tRel - 1).filter((i) => riseAt(fr[i]) >= BODY.SET_FALLBACK_RISE), elbowAt, false, gap);
  const tSet = iSet !== null ? fr[iSet].t : tRel;

  // load: the lowest hips before the set point, or the deepest knees when the hips never moved
  const hipsSeen = (f) => g.vis(f, 23, 24) >= BODY.VIS_LO;
  const loadWin = inWin(tSet - BODY.LOAD_BACK_MS, tSet).filter((i) => hipsSeen(fr[i]));
  let iLoad = null;
  if (loadWin.length) {
    const ys = loadWin.map((i) => g.hMid(fr[i]).y);
    const bp = median(loadWin.map((i) => g.bodyPx(fr[i]))) || 1;
    if (Math.max(...ys) - Math.min(...ys) >= BODY.HIPS_STILL * bp) iLoad = extremeIndex(fr, loadWin, (f) => g.hMid(f).y, true, gap);
    else {
      const knees = (f) => {
        if (g.vis(f, 23, 24, 25, 26, 27, 28) < BODY.VIS_LO) return null;
        const pts = [23, 25, 27, 24, 26, 28].map((i) => g.Q(f, i) || g.P(f, i));
        const a3 = pts.every((p) => fin(p.z)) ? angle3 : angle2;
        return (a3(pts[0], pts[1], pts[2]) + a3(pts[3], pts[4], pts[5])) / 2;
      };
      iLoad = extremeIndex(fr, loadWin, knees, false, gap);
      if (iLoad === null) iLoad = extremeIndex(fr, loadWin, (f) => g.hMid(f).y, true, gap);
    }
  }

  // view at the set point, for the wrist source used to pick the follow through
  const vSet = detectView(fr[iSet ?? iRel], aspect);

  // follow: the most wrist flexion right after release
  const followMs = fin(shot?.metrics?.followMs) ? shot.metrics.followMs : BODY.FOLLOW_MAX_MS;
  const folWin = inWin(tRel, tRel + Math.min(followMs, BODY.FOLLOW_MAX_MS));
  const flexCtx = { g, J, view: vSet?.view, facing: vSet?.facing || 0, aspect };
  const src = wristSource(fr, folWin, flexCtx);
  let iFollow = src ? extremeIndex(fr, folWin, (f) => flexOf(f, flexCtx, src)?.value ?? null, true, gap) : null;
  if (iFollow === null) iFollow = nearestIndex(fr, tRel + BODY.FOLLOW_DEFAULT_MS);

  // feet: lift off and landing
  const feet = iLoad !== null ? footEvents(fr, iLoad, g, gap) : null;
  let tTake = null, tLand = null;
  if (feet) {
    const offs = [feet.L.toeOff, feet.R.toeOff];
    if (offs.every(fin) && Math.max(...offs) - fr[iLoad].t <= BODY.TAKEOFF_MS) tTake = Math.max(...offs);
    if (tTake !== null) {
      downEvents(fr, feet, tTake, g, gap);
      const downs = [feet.L, feet.R].map((ft) => (fin(ft.toeDown) || fin(ft.heelDown) ? Math.min(...[ft.toeDown, ft.heelDown].filter(fin)) : null));
      if (downs.every(fin)) { tLand = Math.max(...downs); feet.L.down = downs[0]; feet.R.down = downs[1]; }
    }
  }
  const iTake = tTake !== null ? nearestIndex(fr, tTake) : null;
  const iLand = tLand !== null ? nearestIndex(fr, tLand) : null;

  // standing hip level, when the capture did not supply one
  let stand = standY;
  if (!fin(stand) && iLoad !== null) stand = percentile(fr.filter((f) => f.t <= fr[iLoad].t && hipsSeen(f)).map((f) => g.hMid(f).y), BODY.STAND_P);

  const idx = { load: iLoad, set: iSet, takeoff: iTake, release: iRel, follow: iFollow, land: iLand };
  const kf = {};
  for (const k of MOMENTS) kf[k] = idx[k] === null ? null : k === 'release' ? { ...fr[idx[k]] } : medianFrame(fr, idx[k]);
  return { side, aspect, gap, fps, frames: fr, idx, kf, t: Object.fromEntries(MOMENTS.map((k) => [k, idx[k] === null ? null : fr[idx[k]].t])), feet, jumped: tTake !== null, landed: tLand !== null, standY: fin(stand) ? stand : null, shot, view: vSet };
}

/** Foot up and down events after the dip (1.3), per foot, in ms. */
function footEvents(fr, iLoad, g, gap) {
  const load = fr[iLoad], tLoad = load.t;
  const b = g.bodyPx(load) || 0.2;
  const need = Math.max(2, Math.round(BODY.FOOT_RUN_MS / gap));
  const feet = {};
  for (const [name, heel, toe] of [['L', 29, 31], ['R', 30, 32]]) {
    const yOf = (f, i) => (f.p[i * 3 + 2] >= BODY.VIS_LO && fin(f.p[i * 3 + 1]) ? f.p[i * 3 + 1] : null);
    const refWin = fr.filter((f) => f.t >= tLoad - BODY.FOOT_REF_MS && f.t <= tLoad);
    const heelRef = percentile(refWin.map((f) => yOf(f, heel)), 0.9) ?? yOf(load, heel);
    const toeRef = yOf(load, toe) ?? percentile(refWin.map((f) => yOf(f, toe)), 0.9);
    const ft = { heelRef, toeRef, heelOff: null, toeOff: null, toeDown: null, heelDown: null, early: false, down: null };
    if (heelRef !== null) ft.heelOff = firstRun(fr, iLoad, fr.length - 1, (f) => { const y = yOf(f, heel); return y === null ? null : (heelRef - y) / b - BODY.FOOT_UP; }, need);
    if (toeRef !== null) ft.toeOff = firstRun(fr, iLoad, fr.length - 1, (f) => { const y = yOf(f, toe); return y === null ? null : (toeRef - y) / b - BODY.FOOT_UP; }, need);
    // Up on the toes before the dip: the heel already up EARLY_HEEL_MS or more before it.
    if (heelRef !== null) {
      let from = null;
      for (let i = iLoad; i >= 0; i--) { const y = yOf(fr[i], heel); if (y === null) continue; if ((heelRef - y) / b >= BODY.FOOT_UP) from = fr[i].t; else break; }
      ft.early = from !== null && tLoad - from >= BODY.EARLY_HEEL_MS;
    }
    feet[name] = ft;
  }
  feet.b = b; feet.need = need; feet.heel = { L: 29, R: 30 }; feet.toe = { L: 31, R: 32 };
  return feet;
}
/** Landing: each point back within FOOT_DOWN of the floor it left, or of where it came to rest. */
function downEvents(fr, feet, tTake, g, gap) {
  const i0 = fr.findIndex((f) => f.t >= tTake);
  if (i0 < 0) return;
  const b = feet.b, need = feet.need;
  const tail = fr.filter((f) => f.t >= fr[fr.length - 1].t - 250);
  for (const name of ['L', 'R']) {
    const ft = feet[name];
    for (const [key, idx, ref] of [['toeDown', feet.toe[name], ft.toeRef], ['heelDown', feet.heel[name], ft.heelRef]]) {
      if (ref === null) continue;
      const yOf = (f) => (f.p[idx * 3 + 2] >= BODY.VIS_LO && fin(f.p[idx * 3 + 1]) ? f.p[idx * 3 + 1] : null);
      const rest = percentile(tail.map(yOf), 0.5);
      // the highest point after lift off: landing is only looked for after it
      let apex = i0, ay = Infinity;
      for (let i = i0; i < fr.length; i++) { const y = yOf(fr[i]); if (y !== null && y < ay) { ay = y; apex = i; } }
      ft[key] = firstRun(fr, apex, fr.length - 1, (f) => {
        const y = yOf(f); if (y === null) return null;
        return Math.max(BODY.FOOT_DOWN - (ref - y) / b, rest !== null ? BODY.FOOT_DOWN - (rest - y) / b : -Infinity);
      }, need);
    }
  }
}
/**
 * When a foot event happened: the first run of `need` seen frames whose margin
 * is 0 or more (margin(f) is how far past the threshold the frame is, null when
 * unseen; unseen frames neither count nor break a run). The time is where the
 * margin crossed 0 between the last frame before the run and its first frame,
 * so an event is placed between frames, not rounded to one: at 15 fps a frame
 * is 66 ms, as long as the whole good band for feet leaving together.
 */
function firstRun(fr, from, to, margin, need) {
  let start = null, n = 0, prev = null;
  for (let i = Math.max(0, from); i <= to && i < fr.length; i++) {
    const m = margin(fr[i]);
    if (m === null) continue;
    if (m >= 0) {
      if (start === null) {
        start = fr[i].t;
        if (prev && prev.m < 0 && m > prev.m) start = prev.t + ((0 - prev.m) / (m - prev.m)) * (fr[i].t - prev.t);
      }
      n++;
      if (n >= need) return start;
    } else { start = null; n = 0; }
    prev = { t: fr[i].t, m };
  }
  return null;
}

/* ======================================================================= *
 *  WRIST FLEXION (the two sources in 1.6)                                  *
 * ======================================================================= */
/** Which wrist source to use across these frames: 'hand' from the side, else 'world'. Null when neither. */
function wristSource(fr, idxs, c) {
  const handOk = (f) => handInfo(f, c).term > 0;
  if ((c.view === 'side' || c.view === 'angled') && c.facing && idxs.length && idxs.filter((i) => handOk(fr[i])).length >= Math.max(1, 0.6 * idxs.length)) return 'hand';
  if (idxs.some((i) => fr[i].w)) return 'world';
  return null;
}
/** Wrist bend in degrees, + toward the rim (flexion), - bent back. */
function flexOf(f, c, source) {
  const { g, J } = c;
  if (!f) return null;
  if (source === 'hand') {
    if (handInfo(f, c).term <= 0 || !c.facing) return null;
    const pe = g.P(f, J.E), pw = g.P(f, J.W), h0 = g.H(f, 0), h9 = g.H(f, 9);
    if (!pe || !pw || !h0 || !h9) return null;
    const u = c.unfold || 1;
    const fa = { x: (pw.x - pe.x) * u, y: pw.y - pe.y }, hd = { x: (h9.x - h0.x) * u, y: h9.y - h0.y };
    const theta = Math.atan2(fa.x * hd.y - fa.y * hd.x, fa.x * hd.x + fa.y * hd.y) * DEG;
    return { value: theta * c.facing, vis: g.vis(f, J.E, J.W), source };
  }
  const qe = g.Q(f, J.E), qw = g.Q(f, J.W), qp = g.Q(f, J.pinky), qi = g.Q(f, J.index), fw = g.frameW(f);
  if (!qe || !qw || !qp || !qi || !fw) return null;
  const fW = norm3(sub3(qw, qe)), hW = norm3(sub3(mid(qp, qi), qw));
  const perp = sub3(hW, { x: fW.x * dot3(hW, fW), y: fW.y * dot3(hW, fW), z: fW.z * dot3(hW, fW) });
  const sign = Math.sign(dot3(perp, fw.f)) || 1;
  return { value: vecAngle3(fW, hW) * sign, vis: g.vis(f, J.E, J.W, J.pinky, J.index), source };
}
/** 1.4 hand term: the hand model's hand for the shooting side, checked against the pose wrist. */
function handInfo(f, c) {
  const { g, J } = c;
  if (!f || !f.h) return { term: 0, reason: 'no-hand' };
  if (f.hs !== null && f.hs !== undefined && f.hs < BODY.HAND_SCORE) return { term: 0, reason: 'no-hand' };
  const h0 = g.H(f, 0), h9 = g.H(f, 9), h5 = g.H(f, 5), h17 = g.H(f, 17), pw = g.P(f, J.W);
  const frameH = c.frameH || 1080;
  // trackmath.handSize is twice wrist to middle knuckle, which shrinks when the
  // hand points at the lens. The knuckle width does not, so the larger of the two
  // (a palm is about 1.5 knuckle widths long) stands for the hand. GUESS.
  const size = h0 && h9 ? 2 * Math.max(dist2(h0, h9), h5 && h17 ? 1.5 * dist2(h5, h17) : 0) * frameH : 0;
  if (pw && size > 0 && dist2(h0, pw) * frameH > BODY.HAND_NEAR_WRIST * size) return { term: 0, reason: 'no-hand', size };
  if (size < BODY.HAND_PX_MIN) return { term: 0, reason: 'hand-small', size };
  return { term: size >= BODY.HAND_PX_OK ? 1 : BODY.HAND_SMALL_TERM, reason: null, size };
}

/* ======================================================================= *
 *  1.6 THE READINGS                                                        *
 * ======================================================================= */
const TRUST_ORDER = ['none', 'low', 'medium', 'high'];
const lowerTrust = (level, by) => TRUST_ORDER[Math.max(0, TRUST_ORDER.indexOf(level) - by)];
const minTrust = (a, b) => TRUST_ORDER[Math.min(TRUST_ORDER.indexOf(a), TRUST_ORDER.indexOf(b))];
/** The camera spot that would unlock a reading, when the current one cannot. */
function betterView(def, view) {
  let best = null;
  for (const v of ['side', 'front', 'back']) {
    if (v === view || TRUST_ORDER.indexOf(def.trust[v]) < 2) continue;
    if (!best || TRUST_ORDER.indexOf(def.trust[v]) > TRUST_ORDER.indexOf(def.trust[best])) best = v;
  }
  return best === 'side' ? 'needs-side' : best ? 'needs-front' : null;
}

/** A reading's 0..100 score from its value (and its modifier: early heels, extra steps). */
export function scoreReading(part, key, value, extra = 0) {
  const d = readingDef(part, key);
  if (!d || !fin(value)) return null;
  const c = d.curve;
  let s;
  if (c[0] === 'code') s = value <= 2 ? 100 : value === 3 ? 55 : 20;
  else if (c[0] === 'onoff') s = value >= 0.5 ? 100 : 20;
  else if (c[0] === 'follow') s = Math.round(clamp((value / SRC.FOLLOW_MS_IDEAL) * 100, 0, 100));
  else if (d.reuse === 'scoreRelease') s = ramp(Math.abs(value - SRC.RELEASE_ANGLE_IDEAL), 0, SRC.RELEASE_ANGLE_ZERO_AT);
  else {
    const v = d.abs ? Math.abs(value) : value;
    s = c[0] === 'ramp' ? ramp(v, c[1], c[2]) : plateau(v, c[1], c[2], c[3]);
  }
  if ((part === 'heels' && key === 'rise' && extra) || (part === 'toes' && key === 'stick' && extra > 0)) s = Math.min(s, BODY.CAP_SCORE);
  return s;
}

/** The good and okay bands of a curve, for the measured row's little bar. */
export function bandsOf(part, key) {
  const d = readingDef(part, key);
  if (!d || !d.band) return null;
  const c = d.curve;
  if (d.reuse === 'scoreRelease') return { domain: d.band, good: [SRC.RELEASE_ANGLE_IDEAL - 12, SRC.RELEASE_ANGLE_IDEAL + 12], okay: [SRC.RELEASE_ANGLE_IDEAL - 24, SRC.RELEASE_ANGLE_IDEAL + 24] };
  if (c[0] === 'follow') return { domain: d.band, good: [0.7 * SRC.FOLLOW_MS_IDEAL, d.band[1]], okay: [0.4 * SRC.FOLLOW_MS_IDEAL, d.band[1]] };
  if (c[0] === 'ramp') {
    const [, best, zero] = c;
    const g = best + 0.3 * (zero - best), o = best + 0.6 * (zero - best);
    const hiBetter = zero < best;
    return { domain: d.band, good: hiBetter ? [g, d.band[1]] : [d.band[0], g], okay: hiBetter ? [o, d.band[1]] : [d.band[0], o] };
  }
  if (c[0] === 'plateau') {
    const [, lo, hi, pad] = c;
    return { domain: d.band, good: [lo - 0.3 * pad, hi + 0.3 * pad], okay: [lo - 0.6 * pad, hi + 0.6 * pad] };
  }
  return null;
}

/**
 * Every reading for one shot (section 1.6), each
 * { value, score, status, conf, reason, rough, extra, source }.
 * `K` is pickKeyframes()'s result. Options: rim { x, y } in frame units,
 * placement 'rim' | 'side' | 'other' | null, frameH the camera height in pixels.
 */
export function readParts(K, { rim = null, placement = null, frameH = 1080 } = {}) {
  if (!K) return null;
  const { aspect, kf, frames: fr, shot, feet, fps } = K;
  const side = K.side, J = SIDES[side], G = SIDES[side === 'left' ? 'right' : 'left'];
  const g = geom(aspect);
  const base = kf.set || kf.release;
  const v = detectView(base, aspect) || { view: 'front', yaw: 0, facing: 0, facingCamera: true };
  // How big the player is in the picture: shoulder width over frame width. Side
  // on, the shoulders overlap in the picture, so the torso stands in for them
  // (a square on player's shoulders are about Q0 torsos wide).
  const swf = Math.max(g.sw(base) || 0, BODY.Q0 * (g.bodyPx(base) || 0)) / aspect;
  const far = swf < BODY.NEAR_SWF;
  // the body turned mid shot: the shoulder line on the floor moved more than TURN_DEG
  const hd = [kf.load, kf.release].map((f) => (f ? detectView(f, aspect)?.heading : null));
  let turned = false;
  for (const h of hd) if (fin(h) && fin(v.heading)) { let d = Math.abs(h - v.heading) % 360; if (d > 180) d = 360 - d; if (d > 90) d = 180 - d; if (d > BODY.TURN_DEG) turned = true; }
  const lat = v.facingCamera ? -1 : 1;           // image x to the player's right
  const rimSq = rim && fin(rim.x) && fin(rim.y) ? { x: rim.x * aspect, y: rim.y } : null;
  const midX = g.sMid(base)?.x;
  const rimLine = placement === 'rim' || Boolean(rimSq && fin(midX) && Math.abs(rimSq.x - midX) <= (g.sw(base) || 0));
  // From the side or at an angle the body's forward direction is foreshortened
  // in the picture by sin(yaw). Picture readings in that plane stretch x back.
  const unfold = v.view === 'side' || v.view === 'angled' ? 1 / Math.max(Math.sin(v.yaw / DEG), 0.5) : 1;
  const c = { g, J, G, aspect, frameH, view: v.view, facing: v.facing, far, fps, turned, kf, fr, shot, unfold };
  const out = {};
  for (const p of PARTS) out[p.id] = {};

  const put = (part, key, raw) => { out[part][key] = finishReading(readingDef(part, key), raw, c); };
  const hidden = { reason: 'joint-hidden' };
  const need = (f, ...idx) => (f && g.vis(f, ...idx) >= BODY.VIS_LO);
  const vOf = (...pairs) => Math.min(...pairs.map(([f, ...idx]) => (f ? g.vis(f, ...idx) : 0)));

  /* ---- FINGERS (hand model, shooting hand) */
  const fingerTarget = (f) => {
    const h0 = g.H(f, 0);
    if (rimSq && h0) { const d = { x: (rimSq.x - h0.x) * unfold, y: rimSq.y - h0.y }; const l = Math.hypot(d.x, d.y); if (l > 1e-6) return { x: d.x / l, y: d.y / l }; }
    if (!v.facing) return null;
    return { x: v.facing * Math.cos(30 / DEG), y: Math.sin(30 / DEG) };
  };
  {
    const f = kf.set; const hi = handInfo(f, c);
    if (!f) put('fingers', 'spread', hidden);
    else if (hi.term <= 0) put('fingers', 'spread', { reason: hi.reason });
    else {
      const H = (j) => g.H(f, j);
      const palm = dist2(H(5), H(17)), len = dist2(H(0), H(9));
      // A palm seen edge on: from the front the ball is in the way; from the
      // side the fingers are stacked one behind the other, and only a phone
      // behind the shooter sees them spread.
      if (!len || palm / len < BODY.PALM_EDGE) put('fingers', 'spread', { reason: v.view === 'side' || v.view === 'angled' ? 'palm-edge' : 'fingers-hidden' });
      else put('fingers', 'spread', { value: dist2(H(8), H(20)) / palm, vis: g.vis(f, J.W), handTerm: hi.term });
    }
  }
  {
    const f = kf.follow; const hi = handInfo(f, c);
    if (!f) { put('fingers', 'lead', hidden); put('fingers', 'point', hidden); }
    else if (hi.term <= 0) { put('fingers', 'lead', { reason: hi.reason }); put('fingers', 'point', { reason: hi.reason }); }
    else {
      const tgt = fingerTarget(f);
      if (!tgt) { put('fingers', 'lead', { reason: betterView(readingDef('fingers', 'lead'), 'front') || 'needs-side' }); put('fingers', 'point', { reason: 'needs-side' }); }
      else {
        const H = (j) => g.H(f, j);
        const fingers = [[5, 6, 8], [9, 10, 12], [13, 14, 16], [17, 18, 20]].map(([m, pp, tip]) => {
          const d = { x: (H(tip).x - H(m).x) * unfold, y: H(tip).y - H(m).y };
          return Math.cos(vecAngle2(d, tgt) / DEG) * (angle2(H(m), H(pp), H(tip)) / 180);
        });
        let best = 0; for (let k = 1; k < 4; k++) if (fingers[k] > fingers[best]) best = k;
        const code = best <= 1 && Math.abs(fingers[0] - fingers[1]) <= 0.05 ? 0 : best + 1;
        put('fingers', 'lead', { value: code, vis: g.vis(f, J.W), handTerm: hi.term });
        put('fingers', 'point', { value: vecAngle2({ x: (H(12).x - H(9).x) * unfold, y: H(12).y - H(9).y }, tgt), vis: g.vis(f, J.W), handTerm: hi.term });
      }
    }
  }
  {
    // goose neck hold: from release, the wrist snaps down past HOLD_FLEX and the
    // hold lasts while it stays there with the arm still up. The snap takes a
    // few frames after release, so the frames before the wrist first gets there
    // do not end the hold. One frame under it is noise; two in a row end it. A
    // wrist that never gets there within the follow window held nothing: 0 ms.
    const i0 = K.idx.release;
    const armUp = (f) => { const s = g.P(f, J.S), e = g.P(f, J.E), w = g.P(f, J.W), d = g.swEff(f); if (!s || !e || !w || !d) return null; return angle2(s, e, w) >= SRC.FOLLOW_ELBOW_ANGLE && (s.y - w.y) / d >= SRC.ARM_RISE * 0.5; };
    const sideOn = v.view === 'side' || v.view === 'angled';
    let start = null, last = null, below = 0, seen = 0, total = 0;
    for (let i = i0; i < fr.length; i++) {
      const f = fr[i];
      if (armUp(f) === false) break;
      total++;
      const fx = sideOn ? flexOf(f, c, 'hand') : null;
      if (!fx) continue;
      seen++;
      if (fx.value >= BODY.HOLD_FLEX) { if (start === null) start = f.t; last = f.t; below = 0; }
      else if (start !== null) { if (++below >= 2) break; }
      else if (f.t - fr[i0].t > BODY.FOLLOW_MAX_MS) break;
    }
    if (total && seen >= Math.max(2, 0.6 * total)) put('fingers', 'hold', { value: start === null ? 0 : last - fr[i0].t, vis: g.vis(fr[i0], J.E, J.W), source: 'hand' });
    else if (fin(shot?.metrics?.followMs)) put('fingers', 'hold', { value: shot.metrics.followMs, vis: g.vis(fr[i0], J.S, J.E, J.W), confCap: BODY.HOLD_ARM_CAP, extra: 1, source: 'arm' });
    else put('fingers', 'hold', { reason: 'no-hand' });
  }

  /* ---- WRIST */
  {
    const one = (keys) => {
      const idxs = keys.map((k) => K.idx[k]);
      if (idxs.some((i) => i === null)) return { reason: 'joint-hidden' };
      const fs = keys.map((k) => kf[k]);
      // the side view with the hand model, else the pose's own knuckles in world points
      let src = (v.view === 'side' || v.view === 'angled') && v.facing && fs.every((f) => handInfo(f, c).term > 0) ? 'hand' : null;
      let vals = src ? fs.map((f) => flexOf(f, c, 'hand')) : null;
      if (!vals || vals.some((x) => !x)) { src = fs.every((f) => f.w) ? 'world' : null; vals = src ? fs.map((f) => flexOf(f, c, 'world')) : null; }
      if (!src || vals.some((x) => !x)) {
        const hi = handInfo(fs[0], c);
        return { reason: v.view === 'front' || v.view === 'back' ? 'needs-side' : hi.reason || 'no-hand' };
      }
      const handTerm = src === 'hand' ? Math.min(...fs.map((f) => handInfo(f, c).term)) : 1;
      return { vals: vals.map((x) => x.value), vis: Math.min(...vals.map((x) => x.vis)), source: src, trustDrop: src === 'world' ? 1 : 0, handTerm };
    };
    const cock = one(['set']);
    put('wrist', 'cock', cock.reason ? cock : { value: -cock.vals[0], vis: cock.vis, source: cock.source, trustDrop: cock.trustDrop, handTerm: cock.handTerm });
    const snap = one(['release', 'follow']);
    put('wrist', 'snap', snap.reason ? snap : { value: snap.vals[1] - snap.vals[0], vis: snap.vis, source: snap.source, trustDrop: snap.trustDrop, handTerm: snap.handTerm });
    const flex = one(['follow']);
    put('wrist', 'flex', flex.reason ? flex : { value: flex.vals[0], vis: flex.vis, source: flex.source, trustDrop: flex.trustDrop, handTerm: flex.handTerm });
  }

  /* ---- ELBOW */
  const armAngle = (f) => {
    const qs = g.Q(f, J.S), qe = g.Q(f, J.E), qw = g.Q(f, J.W);
    if (qs && qe && qw) return { value: angle3(qs, qe, qw), world: true };
    return { value: angle2(g.P(f, J.S), g.P(f, J.E), g.P(f, J.W)), world: false };
  };
  if (!need(kf.set, J.S, J.E, J.W)) put('elbow', 'set', hidden);
  else { const a = armAngle(kf.set); put('elbow', 'set', { value: a.value, vis: g.vis(kf.set, J.S, J.E, J.W), trustDrop: a.world ? 0 : 1 }); }
  {
    const f = kf.release;
    if (!need(f, J.S, J.E, J.W, 11, 12)) put('elbow', 'align', hidden);
    else {
      let value = shot?.metrics?.elbowOffset;
      if (!fin(value)) { const e = g.P(f, J.E), w = g.P(f, J.W); value = (e.x - w.x) / g.sw(f); }
      put('elbow', 'align', { value, vis: g.vis(f, J.S, J.E, J.W, 11, 12) });
    }
  }
  {
    const f = kf.set, fw = f ? g.frameW(f) : null;
    if (!need(f, J.S, J.E) || !fw) put('elbow', 'flare', need(f, J.S, J.E) ? { reason: 'joint-hidden' } : hidden);
    else {
      const e = sub3(g.Q(f, J.E), g.Q(f, J.S));
      const rOut = side === 'right' ? fw.r : { x: -fw.r.x, y: -fw.r.y, z: -fw.r.z };
      put('elbow', 'flare', { value: Math.atan2(dot3(e, rOut), Math.max(dot3(e, fw.f), 0.01)) * DEG, vis: g.vis(f, J.S, J.E), crossWith: 'align' });
    }
  }
  {
    const i0 = K.idx.release; let best = null, vis = 1, world = true;
    for (let i = i0; i < fr.length && fr[i].t <= fr[i0].t + BODY.EXT_MS; i++) {
      if (!need(fr[i], J.S, J.E, J.W)) continue;
      const a = armAngle(fr[i]);
      if (best === null || a.value > best) { best = a.value; vis = g.vis(fr[i], J.S, J.E, J.W); world = a.world; }
    }
    put('elbow', 'ext', best === null ? hidden : { value: best, vis, trustDrop: world ? 0 : 1 });
  }
  {
    const f = kf.release;
    const qe = f && g.Q(f, J.E), qw = f && g.Q(f, J.W), fw = f && g.frameW(f);
    if (!need(f, J.E, J.W)) put('elbow', 'rel', hidden);
    else if (v.view === 'side' || v.view === 'angled') {
      // From the side the picture is the steadier source (the hand and arm are
      // in view, depth is not needed); x is stretched back by the view's yaw.
      const e = g.P(f, J.E), w = g.P(f, J.W);
      put('elbow', 'rel', { value: Math.atan2(-(w.y - e.y), Math.abs(w.x - e.x) * unfold) * DEG, vis: g.vis(f, J.E, J.W), source: 'px' });
    } else if (qe && qw && fw) {
      // The forearm's angle above flat in the body's own forward plane, from
      // world points, so it is the same angle from any camera spot. From the
      // front the forearm points at the lens and its picture reads near 90
      // whatever the real angle is (today's HUD release score has that flaw).
      const fa = sub3(qw, qe), fh = norm3({ x: fw.f.x, y: 0, z: fw.f.z });
      put('elbow', 'rel', { value: Math.atan2(-fa.y, dot3(fa, fh)) * DEG, vis: g.vis(f, J.E, J.W), source: 'world' });
    } else if (shot?.units === 'px' && fin(shot?.metrics?.releaseAngle)) put('elbow', 'rel', { value: shot.metrics.releaseAngle, vis: g.vis(f, J.E, J.W), source: 'reuse' });
    else {
      // No world points: the picture, in square pixels. scoring.js reads this
      // angle in frame units today, which bends it with the camera's shape (spec section 0).
      const e = g.P(f, J.E), w = g.P(f, J.W);
      put('elbow', 'rel', { value: Math.atan2(-(w.y - e.y), Math.abs(w.x - e.x)) * DEG, vis: g.vis(f, J.E, J.W), source: 'px' });
    }
  }
  // cross check: elbow flare may only give a verdict when it agrees with the elbow line to within one tier
  {
    const fl = out.elbow.flare, al = out.elbow.align;
    if (fl.status && al.status && Math.abs(tierOf(fl.status) - tierOf(al.status)) > 1) {
      out.elbow.flare = finishReading(readingDef('elbow', 'flare'), { value: fl.value, vis: fl.vis, confMul: BODY.FLARE_CROSS }, c);
    }
  }

  /* ---- SHOULDERS */
  {
    const f = kf.set;
    if (!need(f, 11, 12)) put('shoulders', 'level', hidden);
    else {
      const a = g.P(f, 11), b = g.P(f, 12);
      const deg = Math.atan2(Math.abs(b.y - a.y), Math.abs(b.x - a.x)) * DEG;
      put('shoulders', 'level', { value: (a.y < b.y ? 1 : -1) * deg, vis: g.vis(f, 11, 12) });
    }
    if (!rimLine) put('shoulders', 'square', { reason: 'no-rim' });
    else if (!need(f, 11, 12)) put('shoulders', 'square', hidden);
    else put('shoulders', 'square', { value: v.yaw, vis: g.vis(f, 11, 12) });
    const fl = kf.load;
    if (!fl || !need(fl, G.S, G.ear) || !need(f, G.S, G.ear)) put('shoulders', 'shrug', hidden);
    else {
      const e = (x) => (g.P(x, G.S).y - g.P(x, G.ear).y) / g.sw(x);
      put('shoulders', 'shrug', { value: e(fl) - e(f), vis: Math.min(g.vis(fl, G.S, G.ear), g.vis(f, G.S, G.ear)) });
    }
  }

  /* ---- HEAD */
  {
    const a = kf.set, b = kf.follow;
    if (!need(a, 0, 11, 12) || !need(b, 0, 11, 12)) put('head', 'still', hidden);
    else {
      const n = (f) => { const s = g.sMid(f), w = g.swEff(f), p = g.P(f, 0); return { x: (p.x - s.x) / w, y: (p.y - s.y) / w }; };
      const na = n(a), nb = n(b);
      put('head', 'still', { value: Math.hypot(nb.x - na.x, nb.y - na.y), vis: Math.min(g.vis(a, 0, 11, 12), g.vis(b, 0, 11, 12)) });
    }
    const r = kf.release;
    if (!rim) put('head', 'rim', { reason: 'no-rim' });
    else {
      const on = eyesOnRim(shot?.metrics, rim);
      // head-square only when the head was read and was square on; no head
      // reading at all (a far player, the ears unseen) must not claim that.
      if (!on) put('head', 'rim', { reason: fin(shot?.metrics?.headYaw) ? 'head-square' : 'joint-hidden' });
      else put('head', 'rim', { value: on === 'on' ? 1 : 0, vis: r ? g.vis(r, 0, 7, 8) : 0 });
    }
  }

  /* ---- CORE */
  {
    const f = kf.release;
    if (!need(f, 11, 12, 23, 24)) { put('core', 'side', hidden); put('core', 'fwd', hidden); }
    else {
      const W = f.w ? [g.Q(f, 11), g.Q(f, 12), g.Q(f, 23), g.Q(f, 24)] : null;
      const s = W ? mid(W[0], W[1]) : g.sMid(f), h = W ? mid(W[2], W[3]) : g.hMid(f);
      // poseFrame(lm).trunkLean, the same maths, signed to the player's right
      const lean = Math.atan2(s.x - h.x, Math.max(h.y - s.y, 1e-6)) * DEG;
      put('core', 'side', { value: lean * lat, vis: g.vis(f, 11, 12, 23, 24) });
      if (!v.facing) put('core', 'fwd', { reason: 'needs-side' });
      else {
        const sp = g.sMid(f), hp = g.hMid(f);
        put('core', 'fwd', { value: Math.atan2((sp.x - hp.x) * v.facing * unfold, hp.y - sp.y) * DEG, vis: g.vis(f, 11, 12, 23, 24) });
      }
    }
  }

  /* ---- HIPS */
  {
    const f = kf.set;
    if (!rimLine) put('hips', 'square', { reason: 'no-rim' });
    else if (!need(f, 23, 24, 11, 12)) put('hips', 'square', hidden);
    else {
      const a = g.P(f, 23), b = g.P(f, 24), bp = g.bodyPx(f);
      const yawI = Math.acos(clamp(dist2(a, b) / bp / BODY.Q0_HIP, 0, 1)) * DEG;
      const qa = g.Q(f, 23), qb = g.Q(f, 24);
      const yawW = qa && qb ? Math.atan2(Math.abs(qb.z - qa.z), Math.abs(qb.x - qa.x)) * DEG : null;
      put('hips', 'square', { value: yawW !== null ? BODY.YAW_WORLD_W * yawW + (1 - BODY.YAW_WORLD_W) * yawI : yawI, vis: g.vis(f, 23, 24) });
    }
    const fl = kf.load;
    const dip = fl && need(fl, 23, 24) && fin(K.standY) ? (g.hMid(fl).y - K.standY) / g.bodyPx(fl) : null;
    // Hips well above standing at the load frame is a bad read of the moment,
    // not a dip: it once said "You sank down -68 percent". Say it was not seen.
    if (dip === null || dip < -0.05) put('hips', 'dip', hidden);
    else put('hips', 'dip', { value: dip, vis: g.vis(fl, 23, 24) });
  }
  // drift: take off to landing; with no jump, the dip to just after the follow through
  const span = () => {
    if (K.jumped && !K.landed) return { reason: 'not-landed' };
    if (K.jumped) return { a: kf.takeoff, b: kf.land };
    if (!kf.load) return { reason: 'joint-hidden' };
    const iEnd = nearestIndex(fr, K.t.follow + BODY.NO_JUMP_END_MS);
    return { a: kf.load, b: fr[iEnd] };
  };
  {
    const sp = span();
    for (const [part, sideKey, fwdKey, pt, idx] of [['hips', 'driftSide', 'driftFwd', 'hMid', [23, 24]], ['toes', 'spotSide', 'spotFwd', 'aMid', [27, 28]]]) {
      if (sp.reason) { put(part, sideKey, { reason: sp.reason }); put(part, fwdKey, { reason: sp.reason }); continue; }
      if (!need(sp.a, ...idx) || !need(sp.b, ...idx)) { put(part, sideKey, hidden); put(part, fwdKey, hidden); continue; }
      const bp = g.bodyPx(sp.a) || g.bodyPx(sp.b);
      const dx = (g[pt](sp.b).x - g[pt](sp.a).x) / bp;
      const vis = Math.min(g.vis(sp.a, ...idx), g.vis(sp.b, ...idx));
      put(part, sideKey, { value: dx * lat, vis });
      put(part, fwdKey, v.facing ? { value: dx * v.facing * unfold, vis } : { reason: 'needs-side' });
    }
  }

  /* ---- KNEES */
  const legIdx = [23, 24, 25, 26, 27, 28];
  const kneeAngles = (f) => {
    const pts = legIdx.map((i) => g.Q(f, i));
    if (pts.every(Boolean)) return { l: angle3(pts[0], pts[2], pts[4]), r: angle3(pts[1], pts[3], pts[5]), world: true };
    const pp = legIdx.map((i) => g.P(f, i));
    return { l: angle2(pp[0], pp[2], pp[4]), r: angle2(pp[1], pp[3], pp[5]), world: false };
  };
  {
    const fl = kf.load;
    if (!fl || !need(fl, ...legIdx)) { put('knees', 'bend', hidden); put('knees', 'track', hidden); put('knees', 'sym', hidden); }
    else {
      let best = null, world = true;
      for (const f of fr) {
        if (f.t < fl.t - BODY.BEND_MS || f.t > fl.t + BODY.BEND_MS || !need(f, ...legIdx)) continue;
        const k = kneeAngles(f); const m = (k.l + k.r) / 2;
        if (best === null || m < best) { best = m; world = k.world; }
      }
      const vis = g.vis(fl, ...legIdx);
      put('knees', 'bend', { value: best, vis, trustDrop: world ? 0 : 1 });
      // knees over toes: how far each knee bows toward the midline off the hip to ankle line
      const hm = g.hMid(fl);
      let worst = 0;
      for (const [h, k, a] of [[23, 25, 27], [24, 26, 28]]) {
        const H = g.P(fl, h), Kp = g.P(fl, k), A = g.P(fl, a);
        const cross = (p) => (A.x - H.x) * (p.y - H.y) - (A.y - H.y) * (p.x - H.x);
        const inward = Math.sign(cross(Kp)) === Math.sign(cross(hm)) && Math.abs(cross(Kp)) > 1e-9;
        const dev = inward ? 180 - angle2(H, Kp, A) : 0;
        if (dev > worst) worst = dev;
      }
      put('knees', 'track', { value: worst, vis });
      const k = kneeAngles(fl);
      put('knees', 'sym', { value: Math.abs(k.l - k.r), vis, trustDrop: k.world ? 0 : 1 });
    }
  }

  /* ---- ANKLES, HEELS */
  const noJump = !K.jumped ? { reason: 'no-jump' } : null;
  const notLanded = K.jumped && !K.landed ? { reason: 'not-landed' } : null;
  {
    if (noJump) put('ankles', 'drive', noJump);
    else {
      const a = kf.load, b = kf.takeoff;
      const idx = [25, 26, 27, 28, 31, 32];
      if (!need(a, ...idx) || !need(b, ...idx)) put('ankles', 'drive', hidden);
      else {
        const ang = (f, k, an, t) => { const q = [g.Q(f, k), g.Q(f, an), g.Q(f, t)]; return q.every(Boolean) ? angle3(...q) : angle2(g.P(f, k), g.P(f, an), g.P(f, t)); };
        const world = Boolean(a.w && b.w);
        const d = ((ang(b, 25, 27, 31) - ang(a, 25, 27, 31)) + (ang(b, 26, 28, 32) - ang(a, 26, 28, 32))) / 2;
        put('ankles', 'drive', { value: d, vis: Math.min(g.vis(a, ...idx), g.vis(b, ...idx)), trustDrop: world ? 0 : 1 });
      }
    }
    const feetVis = (f) => (f ? g.vis(f, 29, 30, 31, 32) : 0);
    if (noJump) { put('ankles', 'offTogether', noJump); put('heels', 'rise', noJump); }
    else {
      put('ankles', 'offTogether', { value: Math.abs(feet.L.toeOff - feet.R.toeOff), vis: feetVis(kf.takeoff) });
      const per = [feet.L, feet.R].map((ft) => ft.toeOff - (fin(ft.heelOff) && ft.heelOff <= ft.toeOff ? ft.heelOff : ft.toeOff));
      put('heels', 'rise', { value: (per[0] + per[1]) / 2, vis: Math.min(feetVis(kf.load), feetVis(kf.takeoff)), extra: feet.L.early || feet.R.early ? 1 : 0 });
    }
    if (noJump || notLanded) { put('ankles', 'landTogether', noJump || notLanded); put('heels', 'land', noJump || notLanded); }
    else {
      put('ankles', 'landTogether', { value: Math.abs(feet.L.down - feet.R.down), vis: feetVis(kf.land) });
      const per = [feet.L, feet.R].map((ft) => {
        const toe = fin(ft.toeDown) ? ft.toeDown : ft.heelDown, heel = fin(ft.heelDown) ? ft.heelDown : ft.toeDown;
        return heel - toe;
      });
      put('heels', 'land', { value: (per[0] + per[1]) / 2, vis: feetVis(kf.land) });
    }
  }

  /* ---- TOES AND FEET */
  {
    const f = kf.load;
    if (!f || !need(f, 27, 28, 11, 12)) put('toes', 'width', hidden);
    else {
      const qa = g.Q(f, 27), qb = g.Q(f, 28), qs = g.Q(f, 11), qt = g.Q(f, 12);
      // poseFrame(lm).ankleSpread: floor distance of the ankles over shoulder width
      const value = qa && qb && qs && qt ? Math.hypot(qa.x - qb.x, qa.z - qb.z) / (len3(sub3(qs, qt)) || 1e-6) : Math.abs(g.P(f, 27).x - g.P(f, 28).x) / g.sw(f);
      put('toes', 'width', { value, vis: g.vis(f, 27, 28, 11, 12) });
    }
    if (!f || !f.w || !need(f, 29, 30, 31, 32)) put('toes', 'point', f && !f.w ? { reason: 'joint-hidden' } : hidden);
    else {
      const d1 = sub3(g.Q(f, 31), g.Q(f, 29)), d2 = sub3(g.Q(f, 32), g.Q(f, 30));
      put('toes', 'point', { value: vecAngle2({ x: d1.x, y: d1.z }, { x: d2.x, y: d2.z }), vis: g.vis(f, 29, 30, 31, 32) });
    }
    if (noJump || notLanded) put('toes', 'stick', noJump || notLanded);
    else {
      const land = K.t.land;
      const win = fr.filter((x) => x.t >= land && x.t <= land + BODY.STICK_MS);
      if (!win.length || win[win.length - 1].t - land < BODY.STICK_MIN_MS) put('toes', 'stick', { reason: 'not-landed' });
      else {
        const b = feet.b || g.bodyPx(kf.land);
        // An extra step is a sole lifting LIFT off its OWN resting level after the
        // landing. Not off the other foot: from the side the far foot sits higher
        // in the picture while both are flat on the floor.
        const seenWin = win.filter((x) => need(x, 27, 28, 29, 30, 31, 32));
        const sole = (x, h, t) => (g.P(x, h).y + g.P(x, t).y) / 2;
        const restL = percentile(seenWin.map((x) => sole(x, 29, 31)), 0.8), restR = percentile(seenWin.map((x) => sole(x, 30, 32)), 0.8);
        // Only a new lift counts: at the landing frame the heels can still be on
        // their way down (toes first), so a foot starts out as it is, not as down.
        const upOf = (x) => [(restL - sole(x, 29, 31)) / b >= SRC.LIFT, (restR - sole(x, 30, 32)) / b >= SRC.LIFT];
        let extra = 0;
        let [upL, upR] = seenWin.length ? upOf(seenWin[0]) : [false, false];
        const offs = [];
        for (const x of seenWin) {
          const [nl, nr] = upOf(x);
          if (nl && !upL) extra++; if (nr && !upR) extra++;
          upL = nl; upR = nr;
          if (x.t <= land + BODY.STICK_OFFSET_MS && need(x, 23, 24)) {
            const la = g.P(x, 27), ra = g.P(x, 28), h = g.hMid(x), am = mid(la, ra);
            offs.push(Math.abs(h.x - am.x) / (Math.max(Math.abs(la.x - ra.x), SRC.MIN_BASE * g.swEff(x)) / 2));
          }
        }
        const off = mean(offs);
        put('toes', 'stick', off === null ? hidden : { value: off, vis: g.vis(kf.land, 23, 24, 27, 28), extra });
      }
    }
  }

  const parts = {};
  for (const p of PARTS) parts[p.id] = partResult(p.id, out[p.id]);
  return {
    readings: out, parts,
    meta: { side, view: v.view, yaw: round(v.yaw, 1), facing: v.facing, facingCamera: v.facingCamera, fps: Math.round(fps), aspect: round(aspect, 3), swf: round(swf, 3), far, rimLine, jumped: K.jumped, landed: K.landed, turned },
  };
}

/** scoring.js eyesOnRim(), copied: 'on', 'off' or null (no rim, or a head square to the camera). */
export function eyesOnRim(metrics, rim) {
  if (!rim || !metrics || !fin(metrics.headYaw) || !fin(metrics.midX)) return null;
  if (Math.abs(metrics.headYaw) < SRC.HEAD_SQUARE) return null;
  const rimSide = Math.sign(rim.x - metrics.midX);
  if (!rimSide) return null;
  return Math.sign(metrics.headYaw) === rimSide ? 'on' : 'off';
}

/** 1.4: one reading's confidence, the gate, and the words for why it could not be read. */
function finishReading(def, raw, c) {
  const blank = { value: null, score: null, status: null, conf: 0, rough: false, reason: null, extra: 0, source: null };
  if (!def) return { ...blank, reason: 'joint-hidden' };
  // 1.7: a reading this camera spot cannot read at all names the spot that
  // can, whatever else went wrong on the shot (no rim marked, a hidden joint),
  // so the player learns where to put the phone first.
  const spot = def.trust[c.view] === 'none' ? betterView(def, c.view) : null;
  // A fine reading on a far player: the distance is the fix to name next,
  // whatever else went wrong (a far hand is also small, hidden or unfound).
  const FAR_FIRST = ['no-hand', 'hand-small', 'fingers-hidden', 'palm-edge', 'joint-hidden', 'needs-side', 'needs-front'];
  if (raw.reason) return { ...blank, reason: spot || (def.fine && c.far && FAR_FIRST.includes(raw.reason) ? 'too-far' : raw.reason) };
  if (!fin(raw.value)) return { ...blank, reason: 'joint-hidden' };
  let level = lowerTrust(def.trust[c.view] || 'none', raw.trustDrop || 0);
  if (c.far) level = minTrust(level, def.trust.far);
  const trust = BODY.TRUST[level];
  const terms = {
    vis: visTerm(raw.vis ?? 1),
    trust,
    size: c.far ? (def.fine ? 0 : BODY.FAR_TERM) : 1,
    fps: def.timing ? (c.fps >= BODY.FPS_OK ? 1 : c.fps >= BODY.FPS_MIN ? BODY.FPS_SLOW_TERM : 0) : 1,
    hand: def.hand ? (raw.handTerm ?? 0) : 1,
    steady: c.turned ? BODY.TURN_TERM : 1,
  };
  let conf = terms.vis * terms.trust * terms.size * terms.fps * terms.hand * terms.steady * (raw.confMul ?? 1);
  if (fin(raw.confCap)) conf = Math.min(conf, raw.confCap);
  conf = round(conf, 3);
  // Why it is not a verdict, in the words of 1.7: the weakest term, and only a
  // reason the player can act on. A reading no camera spot reads well (toe
  // direction) has no fix to offer, so its rough read carries no reason.
  const hint = () => {
    if (spot) return spot;
    if (terms.size === 0) return 'too-far';
    if (terms.fps === 0) return 'low-fps';
    if (terms.vis === 0) return 'joint-hidden';
    const viewHint = betterView(def, c.view);
    if (terms.trust === 0) return viewHint || (c.far ? 'too-far' : 'joint-hidden');
    if (terms.trust <= BODY.TRUST.low && viewHint) return viewHint;
    const weakest = Object.entries(terms).filter(([, x]) => x < 1).sort((a, b) => a[1] - b[1])[0]?.[0];
    return { vis: 'joint-hidden', trust: viewHint, size: 'too-far', fps: 'low-fps', hand: 'hand-small', steady: null }[weakest] ?? null;
  };
  if (conf < BODY.ROUGH) return { ...blank, conf, reason: hint() || 'joint-hidden', source: raw.source ?? null };
  const value = round(raw.value, def.dp ?? 2);
  const extra = raw.extra ?? 0;
  const score = scoreReading(def.part, def.key, value, extra);
  const verdict = conf >= BODY.VERDICT;
  return { value, score, status: verdict ? statusOf(score) : null, conf, rough: !verdict, reason: verdict ? null : hint(), extra, source: raw.source ?? null, vis: raw.vis };
}

/** 2.5: one part on one shot. The weighted mean of its verdict readings. */
export function partResult(partId, readings) {
  const part = PART[partId];
  let sum = 0, wsum = 0, conf = 0, n = 0;
  for (const r of part.readings) {
    const x = readings?.[r.key];
    if (!x || !x.status) continue;
    const w = r.weight ?? 1;
    sum += x.score * w; wsum += w; conf += x.conf; n++;
  }
  if (!n) return { score: null, status: null, conf: 0, n: 0 };
  const score = Math.round(sum / wsum);
  return { score, status: statusOf(score), conf: round(conf / n, 3), n };
}

/* ======================================================================= *
 *  2.2 SNAPSHOT                                                            *
 * ======================================================================= */
const B64 = typeof btoa === 'function' ? btoa : (s) => Buffer.from(s, 'binary').toString('base64');
const UNB64 = typeof atob === 'function' ? atob : (s) => Buffer.from(s, 'base64').toString('binary');
function toB64(bytes) { let s = ''; for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]); return B64(s); }
function fromB64(str) { const s = UNB64(str); const out = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i); return out; }
const I16 = (v) => clamp(Math.round(v), -32768, 32767);

/** One key frame as base64 of a little endian Int16Array (layout in section 2.2). */
export function packFrame(f, tRelease) {
  const hasW = Boolean(f.w), hasH = Boolean(f.h);
  const n = 2 + 63 + (hasW ? 63 : 0) + (hasH ? 63 : 0);
  const dv = new DataView(new ArrayBuffer(n * 2));
  let o = 0;
  const put = (v) => { dv.setInt16(o, I16(fin(v) ? v : 0), true); o += 2; };
  put(f.t - tRelease); put((hasW ? 1 : 0) | (hasH ? 2 : 0));
  for (const i of KEEP_POINTS) { put(f.p[i * 3] * 10000); put(f.p[i * 3 + 1] * 10000); put(f.p[i * 3 + 2] * 100); }
  if (hasW) for (const i of KEEP_POINTS) { put(f.w[i * 3] * 1000); put(f.w[i * 3 + 1] * 1000); put(f.w[i * 3 + 2] * 1000); }
  if (hasH) for (let j = 0; j < 21; j++) { put(f.h[j * 3] * 10000); put(f.h[j * 3 + 1] * 10000); put(f.h[j * 3 + 2] * 10000); }
  return toB64(new Uint8Array(dv.buffer));
}
/** The reverse: a frame in the same shape the engine uses, with only the kept points filled. Throws on a bad length. */
export function unpackFrame(b64) {
  const bytes = fromB64(b64);
  if (bytes.length % 2) throw new Error('odd length');
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const n = bytes.length / 2;
  if (n < 2) throw new Error('too short');
  const flags = dv.getInt16(2, true);
  if (flags < 0 || flags > 3) throw new Error('bad flags');
  const want = 2 + 63 + (flags & 1 ? 63 : 0) + (flags & 2 ? 63 : 0);
  if (n !== want) throw new Error('wrong length');
  let o = 4;
  const get = () => { const v = dv.getInt16(o, true); o += 2; return v; };
  const p = new Float32Array(99).fill(NaN);
  for (let i = 0; i < 33; i++) p[i * 3 + 2] = 0;
  for (const i of KEEP_POINTS) { p[i * 3] = get() / 10000; p[i * 3 + 1] = get() / 10000; p[i * 3 + 2] = get() / 100; }
  let w = null, h = null;
  if (flags & 1) { w = new Float32Array(99).fill(NaN); for (const i of KEEP_POINTS) { w[i * 3] = get() / 1000; w[i * 3 + 1] = get() / 1000; w[i * 3 + 2] = get() / 1000; } }
  if (flags & 2) { h = new Float32Array(63); for (let j = 0; j < 21; j++) { h[j * 3] = get() / 10000; h[j * 3 + 1] = get() / 10000; h[j * 3 + 2] = get() / 10000; } }
  return { t: dv.getInt16(0, true), p, w, h, hs: null };
}

/** Readings for storage: [value, conf 0..100] or [value, conf, extra], 'r:reason' when it could not be read. */
function packReadings(readings) {
  const r = {};
  for (const p of PARTS) {
    const row = {};
    for (const d of p.readings) {
      const x = readings?.[p.id]?.[d.key];
      if (!x) continue;
      if (x.value === null || !fin(x.value)) { if (x.reason) row[d.key] = `r:${x.reason}`; continue; }
      const c = Math.round(x.conf * 100);
      row[d.key] = x.extra ? [x.value, c, x.extra] : [x.value, c];
    }
    if (Object.keys(row).length) r[p.id] = row;
  }
  return r;
}

/** 2.2: the snapshot a shot leaves behind. Numbers only: never video, never an image. */
export function packSnapshot(K, result, meta = {}) {
  const tRel = K.t.release;
  const kf = {};
  for (const k of MOMENTS) kf[k] = K.kf[k] ? packFrame(K.kf[k], tRel) : null;
  const m = result.meta;
  return {
    v: 1, id: meta.id ?? null, side: m.side, view: m.view, fps: m.fps, aspect: m.aspect, swf: m.swf,
    rim: meta.rim && fin(meta.rim.x) && fin(meta.rim.y) ? [Math.round(clamp(meta.rim.x, 0, 1) * 1000), Math.round(clamp(meta.rim.y, 0, 1) * 1000)] : null,
    rl: m.rimLine ? 1 : 0, tv: BODY.TV, kf, r: packReadings(result.readings),
  };
}

/** The key frames and readings back out of a snapshot. Throws on anything malformed. */
export function unpackSnapshot(snap) {
  const ok = validateSnapshot(snap);
  if (!ok.ok) throw new Error(ok.why);
  const s = ok.snap;
  const kf = {};
  for (const k of MOMENTS) kf[k] = s.kf[k] ? unpackFrame(s.kf[k]) : null;
  return { ...s, frames: kf, graded: gradeReadings(s.r) };
}

const REASONS = Object.keys(COPY.reasons);
const byteLength = (str) => (typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(str).length : str.length);
/**
 * For the Worker, before a snapshot is stored: shape, enums, lengths, clamps
 * and size. Returns { ok: true, snap, bytes } with a clean copy (unknown keys
 * dropped, numbers clamped), or { ok: false, why }.
 */
export function validateSnapshot(snap) {
  const no = (why) => ({ ok: false, why });
  if (!snap || typeof snap !== 'object' || Array.isArray(snap)) return no('not an object');
  let text;
  try { text = JSON.stringify(snap); } catch { return no('not serialisable'); }
  const bytes = byteLength(text);
  if (bytes > KEEP.SNAP_MAX_BYTES) return no('too big');
  if (snap.v !== 1) return no('version');
  const num = (v) => typeof v === 'number' && fin(v);
  if (snap.id !== undefined && snap.id !== null && (typeof snap.id !== 'string' || !/^s\d{1,9}:\d{1,5}$/.test(snap.id))) return no('id');
  if (snap.side !== 'left' && snap.side !== 'right') return no('side');
  if (!VIEWS.includes(snap.view)) return no('view');
  for (const k of ['fps', 'aspect', 'swf', 'tv']) if (!num(snap[k])) return no(k);
  if (snap.rim !== null && snap.rim !== undefined && (!Array.isArray(snap.rim) || snap.rim.length !== 2 || !snap.rim.every(num))) return no('rim');
  if (snap.rl !== undefined && snap.rl !== 0 && snap.rl !== 1) return no('rl');
  if (!snap.kf || typeof snap.kf !== 'object' || Array.isArray(snap.kf)) return no('kf');
  for (const k of Object.keys(snap.kf)) if (!MOMENTS.includes(k)) return no(`kf.${k}`);
  const kf = {};
  for (const k of MOMENTS) {
    const x = snap.kf[k];
    if (x === null || x === undefined) { if (k === 'release' || k === 'set' || k === 'follow') return no(`kf.${k} missing`); kf[k] = null; continue; }
    if (typeof x !== 'string' || x.length > 1200 || !/^[A-Za-z0-9+/]+={0,2}$/.test(x)) return no(`kf.${k}`);
    let f;
    try { f = unpackFrame(x); } catch (e) { return no(`kf.${k} ${e.message}`); }
    if (f.t < -5000 || f.t > 5000) return no(`kf.${k} time`);
    kf[k] = x;
  }
  if (!snap.r || typeof snap.r !== 'object' || Array.isArray(snap.r)) return no('r');
  const r = {};
  for (const [part, row] of Object.entries(snap.r)) {
    const p = PART[part];
    if (!p) return no(`r.${part}`);
    if (!row || typeof row !== 'object' || Array.isArray(row)) return no(`r.${part}`);
    const clean = {};
    for (const [key, val] of Object.entries(row)) {
      if (!p.readings.some((d) => d.key === key)) return no(`r.${part}.${key}`);
      if (val === null) { clean[key] = null; continue; }
      if (typeof val === 'string') { if (!val.startsWith('r:') || !REASONS.includes(val.slice(2))) return no(`r.${part}.${key}`); clean[key] = val; continue; }
      if (!Array.isArray(val) || val.length < 2 || val.length > 3 || !val.every(num) || Math.abs(val[0]) > 1e5) return no(`r.${part}.${key}`);
      const c = Math.round(clamp(val[1], 0, 100));
      clean[key] = val.length === 3 ? [val[0], c, Math.round(clamp(val[2], 0, 20))] : [val[0], c];
    }
    r[part] = clean;
  }
  const out = {
    v: 1, id: snap.id ?? null, side: snap.side, view: snap.view,
    fps: Math.round(clamp(snap.fps, 1, 240)), aspect: round(clamp(snap.aspect, 0.2, 5), 3), swf: round(clamp(snap.swf, 0, 1), 3),
    rim: snap.rim ? snap.rim.map((x) => Math.round(clamp(x, 0, 1000))) : null, rl: snap.rl ? 1 : 0,
    tv: Math.round(clamp(snap.tv, 1, 999)), kf, r,
  };
  return { ok: true, snap: out, bytes };
}

/** Stored readings graded now, so a re-tuned threshold re-grades every stored shot. */
export function gradeReadings(r) {
  const out = {};
  for (const p of PARTS) {
    out[p.id] = {};
    for (const d of p.readings) {
      const x = r?.[p.id]?.[d.key];
      if (x === undefined) continue;
      if (x === null) { out[p.id][d.key] = { value: null, score: null, status: null, conf: 0, rough: false, reason: null, extra: 0 }; continue; }
      if (typeof x === 'string') { out[p.id][d.key] = { value: null, score: null, status: null, conf: 0, rough: false, reason: x.slice(2), extra: 0 }; continue; }
      const [value, c, extra = 0] = x;
      const conf = c / 100, score = scoreReading(p.id, d.key, value, extra);
      const verdict = conf >= BODY.VERDICT;
      out[p.id][d.key] = { value, score, status: verdict ? statusOf(score) : null, conf, rough: !verdict, reason: null, extra };
    }
  }
  return out;
}
/** Per part score and status for one stored shot. */
export function shotParts(snapOrGraded) {
  const graded = snapOrGraded?.r ? gradeReadings(snapOrGraded.r) : snapOrGraded;
  const out = {};
  for (const p of PARTS) out[p.id] = partResult(p.id, graded?.[p.id]);
  return out;
}

/* ======================================================================= *
 *  2.4 / 2.5 SESSION TOTALS, SUMMARY                                       *
 * ======================================================================= */
export const blankSession = () => ({ v: 1, tv: BODY.TV, n: 0, n6: 0, ids: [], views: {}, parts: {}, r: {} });
const shotIndexOf = (id) => { const m = typeof id === 'string' ? /:(\d+)$/.exec(id) : null; return m ? Number(m[1]) : null; };

/** Merge one snapshot into a session's totals. Idempotent by shot index. Returns the totals. */
export function mergeSession(agg, snap, { index = null } = {}) {
  const a = agg && agg.v === 1 && Array.isArray(agg.ids) ? agg : blankSession();
  const idx = index ?? shotIndexOf(snap?.id);
  if (!Number.isInteger(idx) || a.ids.includes(idx) || a.ids.length >= KEEP.IDS) return a;
  a.ids.push(idx); a.n++;
  if (VIEWS.includes(snap.view)) a.views[snap.view] = (a.views[snap.view] || 0) + 1;
  const graded = gradeReadings(snap.r);
  let withStatus = 0;
  for (const p of PARTS) {
    const res = partResult(p.id, graded[p.id]);
    if (res.status) {
      withStatus++;
      const row = a.parts[p.id] || (a.parts[p.id] = [0, 0, 0, 0, 0, 0]);
      row[0]++; row[1 + tierOf(res.status)]++; row[4] += res.score; row[5] = round(row[5] + res.conf, 3);
    }
    for (const d of p.readings) {
      const x = graded[p.id][d.key];
      if (!x || !x.status) continue;
      const k = `${p.id}.${d.key}`;
      // [verdict reads, value sum, score sum, reads with an extra (a step after
      // the landing, heels up before the dip)] so a session line never says
      // "You stuck it" over landings that took a step.
      const row = a.r[k] || (a.r[k] = [0, 0, 0, 0]);
      row[0]++; row[1] = round(row[1] + x.value, 3); row[2] += x.score; row[3] = (row[3] || 0) + (x.extra ? 1 : 0);
    }
  }
  if (withStatus >= BODY.STREAK_PARTS) a.n6++;
  return a;
}

/** A session's totals as the screen reads them. */
export function sessionView(agg, { id = null, day = null } = {}) {
  const a = agg && agg.v === 1 ? agg : blankSession();
  const parts = {};
  for (const p of PARTS) {
    const row = a.parts?.[p.id];
    if (!row || !row[0]) { parts[p.id] = { n: 0, g: 0, o: 0, w: 0, score: null, conf: 0, status: null, enough: false }; continue; }
    const [n, gd, ok, wk, ss, cs] = row;
    const score = Math.round(ss / n);
    parts[p.id] = { n, g: gd, o: ok, w: wk, score, conf: round(cs / n, 2), status: statusOf(score), enough: n >= BODY.PART_MIN_SHOTS };
  }
  const readings = {};
  for (const [k, [n, vs, ss, xs = 0]] of Object.entries(a.r || {})) if (n) readings[k] = { n, mean: round(vs / n, 3), score: Math.round(ss / n), x: xs };
  const views = a.views || {};
  const top = Object.entries(views).sort((x, y) => y[1] - x[1]);
  const view = !top.length ? null : top.length > 1 && top[1][1] >= 0.34 * a.n ? 'mixed' : top[0][0];
  return { id, day, n: a.n || 0, n6: a.n6 || 0, view, parts, readings };
}

/**
 * 2.5: per part status, score, trend, best part and next up, from session
 * views oldest first.
 */
export function partSummary(sessions) {
  const list = Array.isArray(sessions) ? sessions : [];
  const latest = list[list.length - 1] || null;
  const parts = {};
  for (const p of PARTS) {
    const q = list.filter((s) => s.parts?.[p.id]?.enough);
    const trend = q.slice(-BODY.TREND_N).map((s) => ({ id: s.id, day: s.day, score: s.parts[p.id].score, status: s.parts[p.id].status }));
    let change = null, arrow = null;
    if (trend.length >= 2) {
      const last = trend[trend.length - 1].score;
      const before = trend.slice(-1 - BODY.TREND_BEFORE, -1).map((x) => x.score);
      change = Math.round(last - mean(before));
      if (Math.abs(change) >= BODY.ARROW) arrow = change > 0 ? 'up' : 'down';
    }
    const pool = q.slice(-BODY.POOL_N);
    const pn = pool.reduce((a, s) => a + s.parts[p.id].n, 0);
    const pooled = pn ? Math.round(pool.reduce((a, s) => a + s.parts[p.id].score * s.parts[p.id].n, 0) / pn) : null;
    const now = latest?.parts?.[p.id] || null;
    parts[p.id] = {
      latest: now, enoughNow: Boolean(now?.enough), status: now?.enough ? now.status : null, score: now?.enough ? now.score : null,
      pooled: pooled === null ? null : { score: pooled, status: statusOf(pooled), sessions: pool.length },
      trend, change, arrow, seenEver: q.length > 0 || list.some((s) => (s.parts?.[p.id]?.n || 0) > 0),
    };
  }
  const pick = (key) => {
    const rows = PARTS.map((p) => ({ id: p.id, s: key === 'today' ? parts[p.id].score : parts[p.id].pooled?.score })).filter((x) => fin(x.s));
    return rows.length ? { best: rows.reduce((a, b) => (b.s > a.s ? b : a)).id, next: rows.reduce((a, b) => (b.s < a.s ? b : a)).id, n: rows.length } : null;
  };
  const today = pick('today'), lately = pick('lately');
  const use = today || lately;
  return { parts, best: use?.best || null, next: use && use.n > 1 && use.next !== use.best ? use.next : null, bestWhen: today ? 'today' : lately ? 'lately' : null, latest };
}

/* ======================================================================= *
 *  3.7 XP, BADGES, STREAK, CELEBRATIONS                                    *
 * ======================================================================= */
export const PART_BADGE = Object.fromEntries(Object.entries(COPY.badges).filter(([, b]) => b.part).map(([id, b]) => [b.part, id]));
const dayNum = (d) => { const t = Date.parse(`${d}T00:00:00Z`); return fin(t) ? Math.round(t / 864e5) : null; };

/**
 * Recomputed from the session totals every time, the way worker/points.js
 * recomputes points: there is no stored number a client can move.
 * `earned` = { badges: [ids already held], celebrated: ['s12:elbow', ...] }.
 * `today` = 'YYYY-MM-DD' (UTC), for the streak.
 */
export function awards(sessions, earned = {}, { today = null } = {}) {
  const list = Array.isArray(sessions) ? sessions : [];
  const had = new Set(earned.badges || []), shown = new Set(earned.celebrated || []);
  let xp = 0;
  const perPart = {}, bestGood = {}, got = new Set(had);
  const prev = {}, pb = {}, sawWork = {};
  for (const s of list) {
    let allGood = PARTS.length > 0;
    for (const p of PARTS) {
      const x = s.parts?.[p.id];
      if (!x?.enough) { allGood = false; continue; }
      let add = Math.min(BODY.XP_GOOD_CAP, BODY.XP_PER_GOOD * (x.g || 0));
      const before = prev[p.id];
      if (before === 'work' && x.status === 'okay') add += BODY.XP_TIER;
      if (before === 'okay' && x.status === 'good') add += BODY.XP_TIER;
      if (before === 'work' && x.status === 'good') add += BODY.XP_JUMP;
      if (pb[p.id] !== undefined && x.score > pb[p.id]) add += BODY.XP_PB;
      pb[p.id] = Math.max(pb[p.id] ?? -Infinity, x.score);
      xp += add; perPart[p.id] = (perPart[p.id] || 0) + add;
      bestGood[p.id] = Math.max(bestGood[p.id] || 0, x.g || 0);
      if ((x.g || 0) >= BODY.BADGE_GOOD) got.add(PART_BADGE[p.id]);
      if (x.status === 'work') sawWork[p.id] = true;
      else if (x.status === 'good' && sawWork[p.id]) got.add('glow-up');
      if (x.status !== 'good') allGood = false;
      prev[p.id] = x.status;
    }
    if (allGood) got.add('head-to-toe');
  }
  // celebrations: the latest session's parts that climbed to good
  const celebrate = [];
  const latest = list[list.length - 1];
  if (latest) {
    for (const p of PARTS) {
      const x = latest.parts?.[p.id];
      if (!x?.enough || x.status !== 'good') continue;
      const before = [...list.slice(0, -1)].reverse().find((s) => s.parts?.[p.id]?.enough)?.parts[p.id].status;
      if (before !== 'work' && before !== 'okay') continue;
      const key = `${latest.id}:${p.id}`;
      if (shown.has(key)) continue;
      celebrate.push({ part: p.id, from: before, to: 'good', big: before === 'work', xp: before === 'work' ? BODY.XP_JUMP : BODY.XP_TIER, key });
    }
  }
  // streak: UTC days in a row with a session that read enough
  const days = [...new Set(list.filter((s) => (s.n6 || 0) >= BODY.STREAK_SHOTS && s.day).map((s) => dayNum(s.day)).filter(fin))].sort((a, b) => a - b);
  let streak = 0;
  const now = today ? dayNum(today) : days[days.length - 1];
  if (days.length && fin(now) && now - days[days.length - 1] <= 1) {
    streak = 1;
    for (let i = days.length - 1; i > 0 && days[i] - days[i - 1] === 1; i--) streak++;
  }
  const level = Math.floor(xp / BODY.XP_LEVEL) + 1;
  const badges = Object.keys(COPY.badges).filter((id) => got.has(id));
  return {
    xp: { total: xp, level, into: xp % BODY.XP_LEVEL, toNext: BODY.XP_LEVEL - (xp % BODY.XP_LEVEL), perPart },
    badges, fresh: badges.filter((id) => !had.has(id)), bestGood, streak, celebrate,
  };
}

/* ======================================================================= *
 *  2.4 THE bm:<uid> RECORD AND GET /api/bodymap (for the Worker)           *
 * ======================================================================= */
export const blankBodymap = () => ({ v: 1, snaps: [], best: {}, celebrated: [], placement: null });

/** A best entry is a snapshot id in the ring, or a full copy once the ring let it go. */
function bestSnap(b, ref) {
  if (typeof ref === 'string') return b.snaps.find((s) => s.id === ref) || null;
  return ref && typeof ref === 'object' ? ref : null;
}

/**
 * Add snapshots that passed validateSnapshot (each with its id) to the
 * bm:<uid> record: a ring of the last KEEP.SNAPS, and the best shot for every
 * part, kept as an id while the ring holds it and copied in full when the
 * ring drops it (at most one copy per part). Idempotent by id. Status is
 * never stored: "best" is re-graded from the stored readings every time.
 */
export function storeSnaps(bm, snaps) {
  const b = bm && bm.v === 1 && Array.isArray(bm.snaps) ? bm : blankBodymap();
  if (!b.best || typeof b.best !== 'object') b.best = {};
  if (!Array.isArray(b.celebrated)) b.celebrated = [];
  for (const s of snaps || []) {
    if (!s || typeof s.id !== 'string') continue;
    const i = b.snaps.findIndex((x) => x.id === s.id);
    if (i >= 0) b.snaps[i] = s; else b.snaps.push(s);
    const parts = shotParts(s);
    for (const p of PART_IDS) {
      if (!parts[p].status) continue;
      const cur = bestSnap(b, b.best[p]);
      const was = cur ? shotParts(cur)[p] : null;
      if (!was?.status || parts[p].score > was.score) b.best[p] = s.id;
    }
  }
  while (b.snaps.length > KEEP.SNAPS) {
    const gone = b.snaps.shift();
    for (const p of Object.keys(b.best)) if (b.best[p] === gone.id) b.best[p] = gone;
  }
  if (b.celebrated.length > KEEP.CELEBRATED) b.celebrated = b.celebrated.slice(-KEEP.CELEBRATED);
  return b;
}

/**
 * Everything render() in public/js/bodymap.js needs, worked out on the spot
 * from what the Worker holds, so no number a client could move is stored.
 *   sessions  the player's sessions, oldest first, each { id, startedAt, bodymap }
 *             (and bodymapModes: { dribble, footwork } once reps are saved)
 *   bm        the bm:<uid> record
 *   held      badge ids the player already has
 *   today     'YYYY-MM-DD' (UTC) for the streak
 * Returns { data, shown }: `shown` are the celebration keys to add to
 * bm.celebrated once this reply has gone out, so each one plays once.
 */
export function bodymapData({ sessions = [], bm = null, held = [], today = null, calibrated = false } = {}) {
  const b = bm && bm.v === 1 && Array.isArray(bm.snaps) ? bm : blankBodymap();
  const dayOf = (s) => (typeof s?.startedAt === 'string' ? s.startedAt.slice(0, 10) : null);
  const all = (Array.isArray(sessions) ? sessions : []).filter((s) => s?.bodymap?.n).map((s) => sessionView(s.bodymap, { id: s.id, day: dayOf(s) }));
  const a = awards(all, { badges: held, celebrated: b.celebrated }, { today });
  const best = {};
  for (const p of PART_IDS) { const s = bestSnap(b, b.best?.[p]); if (s) best[p] = s; }
  const last = b.snaps[b.snaps.length - 1] || null;
  const modes = {};
  for (const mode of ['dribble', 'footwork']) {
    const list = (Array.isArray(sessions) ? sessions : []).filter((s) => s?.bodymapModes?.[mode]?.n).map((s) => sessionView(s.bodymapModes[mode], { id: s.id, day: dayOf(s) }));
    if (list.length) modes[mode] = { sessions: list.slice(-KEEP.VIEW_SESSIONS) };
  }
  return {
    data: {
      side: last?.side === 'left' ? 'left' : 'right', mode: 'shoot', calibrated: Boolean(calibrated),
      sessions: all.slice(-KEEP.VIEW_SESSIONS), snaps: { last, best }, modes,
      xp: a.xp, streak: a.streak, badges: a.badges.map((id) => ({ id })), fresh: a.fresh, bestGood: a.bestGood,
      celebrate: a.celebrate, placement: b.placement ?? null,
    },
    shown: a.celebrate.map((c) => c.key),
  };
}

/* ======================================================================= *
 *  WORDS FOR ONE READING                                                   *
 * ======================================================================= */
const fill = (s, m) => String(s).replace(/\{(\w+)\}/g, (_, k) => (m[k] === undefined ? '' : String(m[k])));
export { fill as fillCopy };
const secs = (ms) => (Math.abs(ms) / 1000).toFixed(2);
/** The "what I measured" sentence for one reading value. */
export function measuredLine(part, key, value, extra = 0) {
  return oneUnit(measuredWords(part, key, value, extra));
}
/** "1 degrees" and friends read as "1 degree". */
const oneUnit = (s) => s.replace(/(^|[^\d.])1 (degree|second|shot|landing|step)s\b/g, '$11 $2');
function measuredWords(part, key, value, extra) {
  const c = COPY.parts[part]?.readings?.[key];
  if (!c || !fin(value)) return '';
  const deg = Math.round(Math.abs(value));
  const pct = (x) => Math.round(Math.abs(x) * 100);
  const lr = (v) => (v >= 0 ? COPY.dirs.right : COPY.dirs.left);
  switch (`${part}.${key}`) {
    case 'fingers.spread': return fill(c.measured, { v: Math.round((value - 1) * 100) });
    case 'fingers.lead': return fill(c.measured, { finger: COPY.fingerNames[clamp(Math.round(value), 0, 4)] });
    case 'fingers.hold': return fill(extra ? c.measuredArm : c.measured, { v: secs(value) });
    case 'elbow.align': return fill(c.measured, { v: pct(value) });
    case 'shoulders.level': return fill(c.measured, { v: deg, side: value >= 0 ? COPY.dirs.left : COPY.dirs.right });
    case 'shoulders.shrug': case 'head.still': return fill(c.measured, { v: Math.max(0, Math.round(value * 100)) });
    case 'head.rim': return fill(c.measured, { dir: value >= 0.5 ? COPY.dirs.toward : COPY.dirs.away });
    case 'core.side': return fill(c.measured, { v: deg, dir: lr(value) });
    case 'core.fwd': return fill(c.measured, { v: deg, dir: value >= 0 ? COPY.dirs.forward : COPY.dirs.back });
    case 'hips.dip': return fill(c.measured, { v: Math.round(value * 100) });
    case 'hips.driftSide': case 'toes.spotSide': return fill(c.measured, { v: pct(value), dir: lr(value) });
    case 'hips.driftFwd': case 'toes.spotFwd': return fill(c.measured, { v: pct(value), dir: value >= 0 ? COPY.dirs.ahead : COPY.dirs.behind });
    case 'ankles.offTogether': case 'ankles.landTogether': return fill(c.measured, { v: secs(value) });
    case 'heels.rise': return fill(extra ? c.measuredEarly : c.measured, { v: secs(value) });
    case 'heels.land': return fill(value < 0 ? c.measuredNeg : c.measured, { v: secs(value) });
    case 'toes.width': return fill(c.measured, { v: Math.abs(value).toFixed(1) });
    case 'toes.stick': return extra > 1 ? fill(c.measuredSteps, { n: extra }) : extra === 1 ? c.measuredStep : c.measured;
    default: return fill(c.measured, { v: deg });
  }
}
/**
 * The measured sentence for a session's average of one reading ({ n, mean, x }
 * from sessionView). Words that belong to single shots (an extra step after
 * the landing, heels up before the dip) are told as a count instead.
 */
export function sessionLine(part, key, agg) {
  if (!agg || !agg.n || !fin(agg.mean)) return '';
  if (part === 'toes' && key === 'stick') return agg.x ? fill(COPY.stickSome, { x: agg.x, n: agg.n }) : COPY.parts.toes.readings.stick.measured;
  const line = measuredLine(part, key, agg.mean, 0);
  if (part === 'heels' && key === 'rise' && agg.x) return `${line} ${fill(COPY.earlySome, { x: agg.x, n: agg.n })}`;
  return line;
}

/** Which way a two sided reading is off, for its needs work words: 'Low' | 'High' | ''. */
export function sideOf(part, key, value) {
  const d = readingDef(part, key);
  if (!d || !fin(value)) return '';
  const c = COPY.parts[part]?.readings?.[key];
  if (!c || (!c.workLow && !c.workHigh)) return '';
  if (d.reuse === 'scoreRelease') return value < SRC.RELEASE_ANGLE_IDEAL ? 'Low' : 'High';
  if (d.curve[0] === 'plateau') return value < d.curve[1] ? 'Low' : 'High';
  return value < 0 ? 'Low' : 'High';
}
/** The headline and tip for a part, from its readings on a shot or a session. */
export function headlineFor(partId, state, lowest = null) {
  const pc = COPY.parts[partId];
  const label = PART[partId].label.toLowerCase();
  if (state === 'good') return { headline: pc.good, tip: null };
  if (state === 'okay') return { headline: pc.okay, tip: lowest ? tipFor(partId, lowest.key, lowest.value) : null };
  if (state === 'work' && lowest) {
    const c = pc.readings[lowest.key];
    const s = sideOf(partId, lowest.key, lowest.value);
    return { headline: c[`work${s}`] || c.work || c.workHigh || c.workLow, tip: tipFor(partId, lowest.key, lowest.value) };
  }
  if (state === 'work') return { headline: pc.okay, tip: null };
  return { headline: fill(COPY.shared[state] || COPY.shared.unseen, { part: label }), tip: null };
}
export function tipFor(partId, key, value) {
  const c = COPY.parts[partId]?.readings?.[key];
  if (!c) return null;
  const s = sideOf(partId, key, value);
  return c[`tip${s}`] || c.tip || c.tipHigh || c.tipLow || null;
}
/** A reason's words, with the part filled in. */
export function reasonLine(reason, partId) {
  const s = COPY.reasons[reason] || COPY.reasons['joint-hidden'];
  return fill(s, { part: (PART[partId]?.label || 'body').toLowerCase() });
}
export function sureWord(conf) {
  return conf >= 0.8 ? COPY.sure.high : conf >= BODY.VERDICT ? COPY.sure.mid : COPY.sure.rough;
}

/* ======================================================================= *
 *  1.9 DRIBBLING AND FOOTWORK, from the reps scoring.js already makes      *
 * ======================================================================= */
const REP_PARTS = {
  dribble: { dribble: { fingers: ['control'], wrist: ['rhythm'] }, balance: { hips: ['centre'] } },
  footwork: {
    stance: { knees: ['depth'], toes: ['width'] }, slide: { knees: ['low'], heels: ['noCross'], toes: ['wide'] },
    drive: { knees: ['low'], hips: ['burst'] }, balance: { hips: ['centre', 'steady'] },
    pivot: { ankles: ['still'] }, footwork: { heels: ['quick', 'balance'] },
  },
};
export const MODE_PARTS = {
  shoot: PART_IDS,
  dribble: ['fingers', 'wrist', 'hips'],
  footwork: ['knees', 'hips', 'ankles', 'heels', 'toes'],
};
/** One rep's per part scores in a mode: { part: score }. */
export function repParts(rep, mode) {
  const map = REP_PARTS[mode]?.[rep?.kind];
  if (!map || !rep.scores) return {};
  const out = {};
  for (const [part, keys] of Object.entries(map)) {
    const v = mean(keys.map((k) => rep.scores[k]).filter(fin));
    if (v !== null) out[part] = Math.round(v);
  }
  return out;
}
/** Reps into a session total (parts only; reps carry no snapshot and no replay in v1). */
export function mergeReps(agg, reps, mode) {
  const a = agg && agg.v === 1 ? agg : blankSession();
  for (const rep of reps || []) {
    const parts = repParts(rep, mode);
    const n = Object.keys(parts).length;
    if (!n) continue;
    a.n++;
    for (const [part, score] of Object.entries(parts)) {
      const row = a.parts[part] || (a.parts[part] = [0, 0, 0, 0, 0, 0]);
      row[0]++; row[1 + tierOf(statusOf(score))]++; row[4] += score; row[5] = round(row[5] + 1, 3);
    }
  }
  return a;
}

/** The score keys each rep kind can bring, from REP_PARTS: anything else a client sends is dropped. */
const REP_KEYS = {};
for (const byKind of Object.values(REP_PARTS)) {
  for (const [kind, map] of Object.entries(byKind)) REP_KEYS[kind] = [...new Set([...(REP_KEYS[kind] || []), ...Object.values(map).flat()])];
}
function cleanRep(r) {
  const keys = REP_KEYS[r?.kind];
  if (!keys || !r.scores || typeof r.scores !== 'object' || Array.isArray(r.scores)) return null;
  const scores = {};
  for (const k of keys) { const v = r.scores[k]; if (typeof v === 'number' && fin(v)) scores[k] = Math.round(clamp(v, 0, 100)); }
  return Object.keys(scores).length ? { kind: r.kind, scores } : null;
}

/**
 * POST /api/sessions/:id/bodymap as pure data, so the Worker's part is a load
 * and a save (the handoff in the build report). `session` is the stored
 * session ({ id, shots: [{ index }] }), `bm` the bm:<uid> record, `body` the
 * request body { seq, snaps: [...], reps: [...] }.
 *  - A snapshot is kept only when it passes validateSnapshot and its id names
 *    this session and one of its saved shots. At most KEEP.BATCH per call.
 *    Merging is idempotent by shot index, so a retried batch counts once.
 *  - Reps are the movement layer's (posemetrics.js onRep), for the Dribbling
 *    and Footwork tabs: kind and scores only, at most KEEP.REPS per call. A
 *    batch carries a sequence number and one the session already took is
 *    skipped, so a retry never counts a rep twice.
 * Changes session.bodymap and session.bodymapModes in place. Returns
 * { bm, saved, reps, dropped, why }.
 */
export function acceptBatch(session, bm, body) {
  const b = bm && bm.v === 1 && Array.isArray(bm.snaps) ? bm : blankBodymap();
  const out = { bm: b, saved: 0, reps: 0, dropped: 0, why: [] };
  if (!session || typeof session.id !== 'string') return out;
  const have = new Set((Array.isArray(session.shots) ? session.shots : []).map((s) => s?.index));
  const good = [];
  for (const raw of Array.isArray(body?.snaps) ? body.snaps.slice(0, KEEP.BATCH) : []) {
    const v = validateSnapshot(raw);
    if (!v.ok) { out.why.push(v.why); continue; }
    if (!v.snap.id || !v.snap.id.startsWith(`${session.id}:`) || !have.has(shotIndexOf(v.snap.id))) { out.why.push('id'); continue; }
    good.push(v.snap);
  }
  for (const s of good) session.bodymap = mergeSession(session.bodymap, s);
  if (good.length) out.bm = storeSnaps(b, good);
  out.saved = good.length;
  const seq = Number.isInteger(body?.seq) ? body.seq : null;
  const reps = (Array.isArray(body?.reps) ? body.reps.slice(0, KEEP.REPS) : []).map(cleanRep).filter(Boolean);
  const modes = session.bodymapModes && typeof session.bodymapModes === 'object' ? session.bodymapModes : {};
  if (reps.length && (seq === null || seq > (modes.seq ?? -1))) {
    for (const mode of ['dribble', 'footwork']) {
      const agg = mergeReps(modes[mode], reps, mode);
      if (agg.n) modes[mode] = agg;
    }
    if (seq !== null) modes.seq = seq;
    session.bodymapModes = modes;
    out.reps = reps.length;
  }
  out.dropped = out.why.length;
  return out;
}

/* ======================================================================= *
 *  CAPTURE: the rolling buffer in the frame loop                           *
 * ======================================================================= */
/**
 * One per camera session.
 *   push(t, lm, hands)   every frame: lm is the smoothed player with lm.world,
 *                        hands [{ side, lm, score }] fresh from the hand model
 *                        on this frame, or null. Never the held hands the
 *                        overlay draws: a held hand is only moved with the
 *                        wrist, so it would fake a still wrist.
 *   onShot(shot, { rim, placement })  -> Promise<{ keyframes, readings, parts, meta, snapshot } | null>
 *   flush()              finish every shot still waiting for its landing
 *   reset({ aspect, height })   a new session or a flipped camera
 * A ring of typed arrays sized for BUFFER_MS at 60 fps: push allocates nothing.
 */
export function createBodyCapture({ aspect = 16 / 9, height = 1080 } = {}) {
  const CAP = Math.ceil((KEEP.BUFFER_MS / 1000) * 60) + 16;
  const tt = new Float64Array(CAP), pp = new Float32Array(CAP * 99), ww = new Float32Array(CAP * 99), hasW = new Uint8Array(CAP);
  const hh = new Float32Array(CAP * 126), hasH = new Uint8Array(CAP * 2), hsc = new Float32Array(CAP * 2);
  let head = 0, count = 0, A = aspect, FH = height;
  const jobs = new Set();

  const slot = (k) => (head - 1 - k + CAP * 2) % CAP;      // k = 0 is the newest frame
  function frameAt(s) {
    const f = { t: tt[s], p: pp.slice(s * 99, s * 99 + 99), w: hasW[s] ? ww.slice(s * 99, s * 99 + 99) : null, hL: null, hR: null, sL: null, sR: null };
    if (hasH[s * 2]) { f.hL = hh.slice(s * 126, s * 126 + 63); f.sL = fin(hsc[s * 2]) ? hsc[s * 2] : null; }
    if (hasH[s * 2 + 1]) { f.hR = hh.slice(s * 126 + 63, s * 126 + 126); f.sR = fin(hsc[s * 2 + 1]) ? hsc[s * 2 + 1] : null; }
    return f;
  }
  function finish(job) {
    if (!jobs.has(job)) return;
    jobs.delete(job);
    if (job.timer) clearTimeout(job.timer);
    let res = null;
    try { res = analyseShot(job.frames, job.shot, { aspect: A, frameH: FH, rim: job.rim, placement: job.placement, standY: job.standY }); }
    catch (e) { res = null; }
    job.resolve(res);
  }
  return {
    get pending() { return jobs.size; },
    get size() { return count; },
    push(t, lm, hands) {
      if (!Array.isArray(lm) || lm.length < 33 || !fin(t)) return;
      const s = head; head = (head + 1) % CAP; count = Math.min(count + 1, CAP);
      tt[s] = t;
      fillPose(pp, lm, s * 99);
      const w = lm.world;
      if (Array.isArray(w) && w.length >= 33 && fin(w[23]?.x)) { fillWorld(ww, w, s * 99); hasW[s] = 1; } else hasW[s] = 0;
      hasH[s * 2] = 0; hasH[s * 2 + 1] = 0;
      if (Array.isArray(hands)) for (const h of hands) {
        const k = h?.side === 'left' ? 0 : h?.side === 'right' ? 1 : -1;
        if (k < 0 || !Array.isArray(h.lm) || h.lm.length < 21) continue;
        fillHand(hh, h.lm, s * 126 + k * 63); hasH[s * 2 + k] = 1; hsc[s * 2 + k] = fin(h.score) ? h.score : NaN;
      }
      for (const job of jobs) {
        job.frames.push(frameAt(s));
        if (t >= job.shot.releasedAt + KEEP.WAIT_LAND_MS) finish(job);
      }
    },
    onShot(shot, { rim = null, placement = null } = {}) {
      if (!shot || !fin(shot.releasedAt) || !count) return Promise.resolve(null);
      const frames = [];
      const hips = [];
      for (let k = count - 1; k >= 0; k--) {
        const s = slot(k);
        const t = tt[s];
        if (t >= shot.releasedAt - KEEP.PRE_MS) frames.push(frameAt(s));
        // the standing hip level, as createLevelTracker({ windowMs: 4000, p: 0.6 }) would hold it
        if (t >= shot.releasedAt - BODY.STAND_MS && t <= shot.releasedAt - 700 && pp[s * 99 + 23 * 3 + 2] >= BODY.VIS_LO && pp[s * 99 + 24 * 3 + 2] >= BODY.VIS_LO) hips.push((pp[s * 99 + 23 * 3 + 1] + pp[s * 99 + 24 * 3 + 1]) / 2);
      }
      return new Promise((resolve) => {
        const job = { shot, rim, placement, frames, standY: hips.length >= 5 ? percentile(hips, BODY.STAND_P) : null, resolve, timer: null };
        jobs.add(job);
        const newest = tt[slot(0)];
        if (newest >= shot.releasedAt + KEEP.WAIT_LAND_MS) { finish(job); return; }
        // frames may stop (the session ended): finish with what there is
        if (typeof setTimeout === 'function') {
          job.timer = setTimeout(() => finish(job), KEEP.WAIT_LAND_MS + 1500);
          job.timer?.unref?.();
        }
      });
    },
    flush() { const all = [...jobs]; all.forEach(finish); return Promise.resolve(all.length); },
    reset({ aspect: a2, height: h2 } = {}) {
      for (const job of [...jobs]) { jobs.delete(job); if (job.timer) clearTimeout(job.timer); job.resolve(null); }
      head = 0; count = 0;
      if (fin(a2) && a2 > 0) A = a2;
      if (fin(h2) && h2 > 0) FH = h2;
    },
  };
}

/** Frames around a shot to the full result: key frames, readings, parts and the snapshot. */
export function analyseShot(frames, shot, { aspect = 16 / 9, frameH = 1080, rim = null, placement = null, standY = null } = {}) {
  const K = pickKeyframes(frames, shot, { aspect, standY });
  if (!K) return null;
  const res = readParts(K, { rim, placement, frameH });
  const snapshot = packSnapshot(K, res, { rim });
  return { keyframes: K, readings: res.readings, parts: res.parts, meta: res.meta, snapshot };
}
